import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  AppWindow,
  Archive,
  Bot,
  CheckCircle2,
  Code2,
  Database,
  Eye,
  FileCode2,
  FolderGit2,
  Globe2,
  History,
  Monitor,
  MousePointer2,
  PanelLeftClose,
  PanelLeftOpen,
  Paperclip,
  Plus,
  RefreshCcw,
  Rocket,
  Save,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Smartphone,
  XCircle
} from "lucide-react";
import {
  buildPromptQueue,
  promptQueueRefreshInterval,
  promptQueueSummary,
  resolvePrimaryPromptRun
} from "./promptQueue.js";
import { buildActionJobTranscriptEntries, buildManualActionEventTranscriptEntries, mergeTranscriptItems } from "./chatTimeline.js";
import "./styles.css";

const visualSelectionPattern = /(?:\n\n)?Edição visual:\n- Ajustar a área selecionada no preview:[\s\S]*?- Use a seleção visual como referência de layout; preserve o restante da tela\./g;

const api = async (url, options = {}) => {
  const response = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options
  });
  const payload = await response.json().catch(() => ({}));
  if (response.status === 401 && typeof window !== "undefined") {
    window.dispatchEvent(new Event("axqi-auth-expired"));
  }
  if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
  return payload;
};

function flattenTree(items, depth = 0) {
  return items.flatMap((item) => [
    { ...item, depth },
    ...(item.children ? flattenTree(item.children, depth + 1) : [])
  ]);
}

function runStatusMeta(status, item = null) {
  if (status === "queued") return { label: "Na fila", detail: "Preparando a execução", tone: "pending" };
  if (status === "running") return { label: "Trabalhando", detail: "Codex está executando agora", tone: "running" };
  if (status === "completed") return { label: "Concluído", detail: "Resultado pronto", tone: "ok" };
  if (status === "failed" && item?.commit_sha) return { label: "Publicação pendente", detail: "Alteração salva; aguardando publicação", tone: "warn" };
  if (status === "failed") return { label: "Falhou", detail: "Precisa de revisão", tone: "warn" };
  if (status === "canceled") return { label: "Cancelado", detail: "Execução interrompida", tone: "idle" };
  return { label: status || "Registrado", detail: "Execução registrada", tone: "idle" };
}

function activityLabel(type) {
  if (type === "prompt") return "Codex";
  if (type === "deployment") return "Publicação";
  if (type === "file") return "Arquivo";
  return "Ação";
}

function operationLabel(action) {
  if (action === "create_project") return "Novo projeto";
  if (action === "preview") return "Preview automático";
  if (action === "deploy") return "Publicação";
  if (action === "build") return "Build";
  if (action === "test") return "Validação";
  if (action === "db_status") return "DB status";
  if (action === "db_migrate") return "Migrações";
  if (action === "db_seed") return "Seed";
  if (action === "db_logs") return "Logs do banco";
  if (action === "db_start") return "Iniciar banco";
  if (action === "db_stop") return "Parar banco";
  if (action === "status") return "Status";
  return action || "Operação";
}

function slugifyProjectName(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

function profileDraftFromWorkflow(workflow) {
  return {
    previewStrategy: workflow?.previewStrategy || "auto",
    publishStrategy: workflow?.publishStrategy || "auto",
    dbKind: workflow?.dbKind || "auto",
    migrationCmd: workflow?.migrationCmd || "",
    seedCmd: workflow?.seedCmd || "",
    dbContainer: workflow?.dbContainer || "",
    runtimeServicesText: Array.isArray(workflow?.runtimeServices) ? workflow.runtimeServices.join("\n") : "",
    validationCmdsText: Array.isArray(workflow?.validationCmds) ? workflow.validationCmds.join("\n") : "",
    notes: workflow?.notes || ""
  };
}

function tableSecurityClass(table) {
  if (!table.kind?.includes("table")) return "neutral";
  return table.rls ? "ok" : "warn";
}

function tableSecurityLabel(table) {
  if (!table.kind?.includes("table")) return "view";
  return table.rls ? "RLS ativo" : "RLS off";
}

function formatClientAccess(table) {
  const access = table.clientAccess || {};
  const enabled = ["anon", "authenticated"].flatMap((role) =>
    Object.entries(access[role] || {})
      .filter(([, value]) => Boolean(value))
      .map(([action]) => `${role}:${action}`)
  );
  return enabled.length ? enabled.join(", ") : "sem grants anon/auth";
}

function wasRecovered(item) {
  return item?.progress_stage === "recovered";
}

function cleanChatOutput(text) {
  const blocked = [
    /node_modules existente/i,
    /^>\s*(build|vite|npm)/i,
    /failed to load config/i,
    /error during build/i,
    /ERR_MODULE_NOT_FOUND/i,
    /node:internal/i,
    /ModuleLoader/i,
    /package_json_reader/i,
    /vite\.config/i,
    /npm ERR!/i,
    /@lovable\.dev/i,
    /local\s*storage/i,
    /Pr[oó]ximos passos/i,
    /limpar cache/i,
    /hard refresh/i,
    /Se quiser/i,
    /Observa[cç][aã]o importante/i,
    /não consegui validar/i,
    /nao consegui validar/i,
    /ambiente local/i,
    /Reading additional input/i,
    /OpenAI Codex/i,
    /tokens used/i,
    /bubblewrap/i
  ];
  const technicalLine = /^(\[?\d{0,2}:?\d{2}:\d{2}\]?\s*)?(Codex:\s*)?(\s{2,}|const |let |var |function |return |if \(|for \(|while \(|import |export |await |async |try \{|catch \(|throw |class |type |interface |\/\/|\/\*|\*\/|[{}()[\];]|at |Require stack:|Error: Cannot find module|failed to load config|error during build|npm warn|npm error|node:internal|file:\/\/|\/srv\/|<\/?|NODE'|bash -lc|Comando concluido:|Executando comando:)/;
  const kept = [];
  let skippingTechnicalBlock = false;
  for (const rawLine of String(text || "")
    .replace(/\u001b\[[0-9;]*m/g, "")
    .split(/\r?\n/)
    .map((line) => line.trimEnd())) {
    const line = rawLine.trim();
    if (!line) {
      skippingTechnicalBlock = false;
      continue;
    }
    if (blocked.some((pattern) => pattern.test(line)) || technicalLine.test(line)) {
      skippingTechnicalBlock = true;
      continue;
    }
    if (skippingTechnicalBlock && /^[-–•]/.test(line)) continue;
    skippingTechnicalBlock = false;
    kept.push(line.replace(/^\[\d{2}:\d{2}:\d{2}\]\s*/, ""));
  }
  const value = kept.join("\n").trim();
  if (/publicacao automatica falhou|publicação automática falhou/i.test(String(text || ""))) {
    return [value, "A alteração foi salva. A publicação automática está sendo corrigida pelo AXQI Editor."].filter(Boolean).join("\n");
  }
  return value;
}

function publicationTone(state = "") {
  if (state === "published") return "ok";
  if (state === "stale" || state === "unverified") return "warn";
  if (state === "unhealthy") return "error";
  return "idle";
}

function publicationPrimaryAction(publication) {
  const nextAction = publication?.nextAction || "manual_review";
  if (nextAction === "publish") {
    return { label: "Publicar", title: publication?.detail || "Abrir painel de publicação", kind: "publish" };
  }
  if (nextAction === "check_logs") {
    return { label: "Ver status", title: publication?.detail || "Abrir atividade operacional", kind: "logs" };
  }
  if (nextAction === "manual_review") {
    return { label: "Revisar", title: publication?.detail || "Abrir revisão operacional do projeto", kind: "review" };
  }
  return { label: "Publicado", title: publication?.detail || "Abrir painel de publicação", kind: "published" };
}

function publicationToolbarAction(publication) {
  const nextAction = publication?.nextAction || "manual_review";
  if (nextAction === "publish") return { label: "Publicar", kind: "publish" };
  if (nextAction === "check_logs") return { label: "Atividade", kind: "logs" };
  if (nextAction === "manual_review") return { label: "Revisar projeto", kind: "review" };
  return { label: "Status", kind: "status" };
}

function publicationModalAction(publication) {
  const nextAction = publication?.nextAction || "manual_review";
  if (nextAction === "publish") {
    return { label: "Confirmar publicação", kind: "publish", disabled: false };
  }
  if (nextAction === "check_logs") {
    return { label: "Abrir atividade", kind: "logs", disabled: false };
  }
  if (nextAction === "manual_review") {
    return { label: "Revisar status", kind: "review", disabled: false };
  }
  return { label: "Já publicado", kind: "published", disabled: true };
}

function projectValidationAction(validation, workflow) {
  if (validation?.configured) {
    return {
      kind: "test",
      local: Boolean(validation.localSupported),
      label: validation.actionLabel || (validation.localSupported ? "Validar local" : "Validar projeto"),
      detail: validation.detail || "Executa a validação configurada deste projeto.",
      note: validation.note || "O resultado aparece no chat e na atividade assim que a validação entrar na fila.",
      state: validation.state || "pending",
      statusLabel: validation.statusLabel || "Validação pendente",
      recommended: Boolean(validation.recommended),
      lastRun: validation.lastRun || null
    };
  }
  const commands = Array.isArray(workflow?.validationCmds) ? workflow.validationCmds.filter(Boolean) : [];
  if (!commands.length) return null;
  const local = commands.some((command) => /\b(?:npm|pnpm|bun)\s+run\s+test:e2e:local\b/i.test(command));
  return {
    kind: "test",
    local,
    label: local ? "Validar local" : "Validar projeto",
    detail: local
      ? "Confere este projeto localmente antes de publicar."
      : "Executa a validação configurada deste projeto.",
    note: local
      ? "O resultado aparece no chat e na atividade assim que a validação entrar na fila."
      : "Usa o workflow de validação configurado para este projeto.",
    state: "pending",
    statusLabel: "Validação pendente",
    recommended: false,
    lastRun: null
  };
}

function dataFocusModel(publication, blockers = []) {
  if (!publication) return null;
  if (publication.nextAction === "manual_review") {
    return {
      tone: publicationTone(publication.state),
      title: publication.reasonLabel || "Revisão operacional necessária",
      detail: blockers.length
        ? `Confira primeiro: ${blockers.map((gate) => gate.label).join(", ")}.`
        : publication.detail,
      target: blockers.length ? "gates" : (publication.targetSection || "summary"),
      actionLabel: blockers.length ? "Ver checks técnicos" : "Ver publicação",
      actionKind: blockers.length ? "gates" : "publication"
    };
  }
  if (publication.nextAction === "check_logs") {
    return {
      tone: publicationTone(publication.state),
      title: publication.reasonLabel || "Diagnóstico operacional",
      detail: publication.detail,
      target: publication.targetSection || "activity",
      actionLabel: "Abrir atividade",
      actionKind: "logs"
    };
  }
  if (publication.nextAction === "publish") {
    return {
      tone: publicationTone(publication.state),
      title: publication.reasonLabel || "Pronto para publicar",
      detail: publication.detail,
      target: publication.targetSection || "summary",
      actionLabel: "Abrir publicação",
      actionKind: "publish"
    };
  }
  return null;
}

function guidedFocusTarget(publication, actionKind) {
  if (actionKind === "logs") {
    return {
      tab: publication?.targetTab === "logs" ? "logs" : "logs",
      section: publication?.targetSection || "activity"
    };
  }
  if (actionKind === "review") {
    return {
      tab: publication?.targetTab === "data" ? "data" : "data",
      section: publication?.targetSection || "summary"
    };
  }
  if (actionKind === "gates") {
    return { tab: "data", section: "gates" };
  }
  if (actionKind === "publication") {
    return { tab: "data", section: publication?.targetSection || "summary" };
  }
  return null;
}

function resolveFocusedActivityId(items = [], publication = null) {
  if (!Array.isArray(items) || !items.length) return "";
  if (publication?.primaryRef?.kind === "activity") {
    if (publication.primaryRef.id && items.some((item) => item.id === publication.primaryRef.id)) {
      return publication.primaryRef.id;
    }
  }
  if (publication?.nextAction === "check_logs") {
    return items.find((item) => item.status === "failed")?.id
      || items.find((item) => item.status === "running")?.id
      || items.find((item) => item.status === "queued")?.id
      || items[0]?.id
      || "";
  }
  return "";
}

function resolveFocusedDeploymentId(items = [], publication = null) {
  if (!Array.isArray(items) || !items.length) return null;
  const id = publication?.primaryRef?.kind === "deployment"
    ? publication.primaryRef.id
    : publication?.deploymentRef?.id;
  return id && items.some((item) => String(item.id) === String(id)) ? id : null;
}

function riskTargetFromPublicationRisk(risk = null) {
  if (!risk) return null;
  if (risk.source === "gate" || risk.gateRef) {
    return {
      tab: "data",
      section: risk.targetSection || (risk.gateRef?.key === "supabase" ? "db" : "gates"),
      gateKey: risk.gateRef?.key || null
    };
  }
  if (risk.source === "publication" || risk.deploymentRef) {
    return {
      tab: "data",
      section: risk.targetSection || "deployments",
      deploymentId: risk.deploymentRef?.id || null
    };
  }
  if (risk.source === "activity" || risk.activityRef) {
    return {
      tab: "logs",
      section: risk.targetSection || "activity",
      activityId: risk.activityRef?.id || null
    };
  }
  return null;
}

function publicationRiskSummary(publication = null) {
  return "";
}

function drawerPublicationAction(publication = null) {
  if (!publication) return null;
  if (publication.nextAction === "check_logs") return { label: "Ver atividade", kind: "logs" };
  if (publication.nextAction === "publish") return { label: "Ver publicação", kind: "publish" };
  if (publication.nextAction === "manual_review") {
    if (publication.targetSection === "deployments") return { label: "Ver versões", kind: "primary_context" };
    if (publication.targetSection === "db") return { label: "Ver banco", kind: "primary_context" };
    return { label: "Ver checks", kind: "primary_context" };
  }
  return { label: "Ver detalhes", kind: "primary_context" };
}

function chatPublicationAction(overview = null) {
  if (!overview) return null;
  if (overview.actionKind === "run_validation") return { label: overview.actionLabel || "Validar projeto", kind: "validate" };
  if (overview.actionKind === "logs") return { label: overview.actionLabel || "Ver atividade", kind: "logs" };
  if (overview.actionKind === "publish") return { label: overview.actionLabel || "Ver publicação", kind: "publish" };
  if (overview.actionKind === "primary_context") return { label: overview.actionLabel || "Ver detalhes", kind: "primary_context" };
  return { label: overview.actionLabel || "Atualizar status", kind: "status" };
}

function explicitTurnNextStep(item = null) {
  if (!item?.nextStep?.kind) return null;
  return {
    kind: item.nextStep.kind,
    label: item.nextStep.label || "Continuar",
    detail: item.nextStep.detail || ""
  };
}

function turnOwnsPrimaryAction(publication = null, sourceKind = "", sourceId = null) {
  if (!publication || !sourceKind || sourceId == null) return false;
  if (publication?.validation?.recommended && publication?.validation?.ownerTurnRef) {
    return publication.validation.ownerTurnRef.sourceKind === sourceKind
      && String(publication.validation.ownerTurnRef.sourceId || "") === String(sourceId);
  }
  return publication?.turnRef?.sourceKind === sourceKind
    && String(publication.turnRef.sourceId || "") === String(sourceId);
}

function terminalRunCompletionAction(run, publication = null) {
  if (!run || ["queued", "running", "canceled"].includes(run.status || "")) return null;
  if ((run.mode || "edit") === "plan") return null;
  if (publication?.validation?.configured && publication?.validation?.recommended) {
    return { label: publication.validation.actionLabel || "Validar projeto", kind: "validate" };
  }
  if (publication?.nextAction === "publish") return { label: "Publicar", kind: "publish" };
  if (publication?.nextAction === "check_logs") return { label: "Ver atividade", kind: "logs" };
  if (publication?.nextAction === "manual_review") {
    if (publication.targetSection === "deployments") return { label: "Ver versões", kind: "primary_context" };
    if (publication.targetSection === "db") return { label: "Ver banco", kind: "primary_context" };
    return { label: "Ver checks", kind: "primary_context" };
  }
  if (publication?.nextAction === "none") return { label: "Ver publicação", kind: "primary_context" };
  return null;
}

function terminalRunPreviewAction(run, actionJobs = []) {
  if (!run || ["queued", "running", "canceled"].includes(run.status || "")) return null;
  if ((run.mode || "edit") === "plan") return null;
  const previewJob = [...(Array.isArray(actionJobs) ? actionJobs : [])]
    .filter((job) =>
      job?.action === "preview"
      && job?.status === "completed"
      && job?.result?.previewUrl
      && (
        (job.origin_kind === "prompt_run" && String(job.origin_id || "") === String(run.id))
        || new Date(job.created_at || job.updated_at || 0).getTime() >= new Date(run.created_at || 0).getTime()
      )
    )
    .sort((a, b) => new Date(b.completed_at || b.updated_at || b.created_at || 0).getTime() - new Date(a.completed_at || a.updated_at || a.created_at || 0).getTime())[0];
  if (!previewJob?.result?.previewUrl) return null;
  return { label: "Ver preview", kind: "preview", previewUrl: previewJob.result.previewUrl };
}

function terminalJobCompletionAction(job, publication = null) {
  if (!job || ["queued", "running", "canceled"].includes(job.status || "")) return null;
  if (publication?.validation?.configured && publication?.validation?.recommended) {
    return { label: publication.validation.actionLabel || "Validar projeto", kind: "validate" };
  }
  if (publication?.nextAction === "publish") return { label: "Publicar", kind: "publish" };
  if (publication?.nextAction === "check_logs") return { label: "Ver atividade", kind: "logs" };
  if (publication?.nextAction === "manual_review") {
    if (publication.targetSection === "deployments") return { label: "Ver versões", kind: "primary_context" };
    if (publication.targetSection === "db") return { label: "Ver banco", kind: "primary_context" };
    return { label: "Ver checks", kind: "primary_context" };
  }
  if (publication?.nextAction === "none") return { label: "Ver publicação", kind: "primary_context" };
  return null;
}

function App() {
  const [auth, setAuth] = useState({ loading: true, authenticated: false, user: null });
  const [loginForm, setLoginForm] = useState({ username: "admin", password: "" });
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [query, setQuery] = useState("");
  const [tree, setTree] = useState([]);
  const [filePath, setFilePath] = useState("");
  const [fileContent, setFileContent] = useState("");
  const [diff, setDiff] = useState("");
  const [infra, setInfra] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState("plan");
  const [model, setModel] = useState("gpt-5.4");
  const [reasoningEffort, setReasoningEffort] = useState("medium");
  const [models, setModels] = useState([]);
  const [reasoningLevels, setReasoningLevels] = useState([]);
  const [settings, setSettings] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentNotice, setAttachmentNotice] = useState("");
  const [activeTab, setActiveTab] = useState("preview");
  const [draftPreviewUrl, setDraftPreviewUrl] = useState("");
  const [previewRefreshToken, setPreviewRefreshToken] = useState("");
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [visualEditMode, setVisualEditMode] = useState(false);
  const [visualSelection, setVisualSelection] = useState(null);
  const [runs, setRuns] = useState([]);
  const [promptQueueRuns, setPromptQueueRuns] = useState([]);
  const [actionEvents, setActionEvents] = useState([]);
  const [actionJobs, setActionJobs] = useState([]);
  const [chatTranscript, setChatTranscript] = useState([]);
  const [chatTranscriptProjectId, setChatTranscriptProjectId] = useState("");
  const [chatTranscriptReady, setChatTranscriptReady] = useState(false);
  const [deployments, setDeployments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [usage, setUsage] = useState(null);
  const [showPublish, setShowPublish] = useState(false);
  const [showProjectConfig, setShowProjectConfig] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showImportProject, setShowImportProject] = useState(false);
  const [configDraft, setConfigDraft] = useState(null);
  const [createProjectDraft, setCreateProjectDraft] = useState({ name: "", slug: "", prompt: "", slugTouched: false });
  const [importProjectDraft, setImportProjectDraft] = useState({ name: "", slug: "", repositoryUrl: "", prompt: "", slugTouched: false });
  const [savingConfig, setSavingConfig] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [importingProject, setImportingProject] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [guidedFocus, setGuidedFocus] = useState(null);
  const [log, setLog] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const runsRef = useRef(null);
  const visualDragRef = useRef(null);
  const chatInputComposingRef = useRef(false);
  const promptSubmitPendingRef = useRef(false);
  const promptQueuePulseTimersRef = useRef([]);
  const [composerSubmitting, setComposerSubmitting] = useState(false);
  const preferencesReadyRef = useRef(false);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const selected = projects.find((project) => project.id === selectedId);
  const activeRun = useMemo(() => resolvePrimaryPromptRun(runs), [runs]);
  const hasActiveRuns = Boolean(activeRun);
  const activeActionJob = actionJobs.find((job) => ["queued", "running"].includes(job.status));
  const hasActiveActionJobs = actionJobs.some((job) => ["queued", "running"].includes(job.status));
  const isSelfEditorPreview = selected?.name === "editor" && selected?.domain === "editor.axqi.ai";
  const criticalPublishBlockers = (infra?.gates || []).filter((gate) => ["dirty", "github", "origin", "remote_sync"].includes(gate.key) && !gate.ok);
  const publication = infra?.publication || null;
  const publicationStateTone = publicationTone(publication?.state);
  const primaryPublicationAction = publicationPrimaryAction(publication);
  const toolbarPublicationAction = publicationToolbarAction(publication);
  const modalPublicationAction = publicationModalAction(publication);
  const validationAction = projectValidationAction(publication?.validation, infra?.workflow);
  const dataFocus = dataFocusModel(publication, criticalPublishBlockers);
  const activityOverview = publication?.activityOverview || null;
  const chatOverview = publication?.chatOverview || null;
  const publicationRiskText = publicationRiskSummary(publication);
  const projectDrawerAction = drawerPublicationAction(publication);
  const chatOverviewAction = chatPublicationAction(chatOverview);
  const focusedGateKey = guidedFocus?.gateKey || publication?.gateRef?.key || "";
  const focusedDataSection = guidedFocus?.tab === "data"
    ? guidedFocus.section
    : (dataFocus?.target || publication?.targetSection || "");
  const focusedActivityId = useMemo(() => {
    if (guidedFocus?.activityId && activity.some((item) => item.id === guidedFocus.activityId)) {
      return guidedFocus.activityId;
    }
    return resolveFocusedActivityId(activity, publication);
  }, [activity, guidedFocus, publication]);
  const focusedDeploymentId = useMemo(() => {
    if (guidedFocus?.deploymentId && deployments.some((item) => String(item.id) === String(guidedFocus.deploymentId))) {
      return guidedFocus.deploymentId;
    }
    return resolveFocusedDeploymentId(deployments, publication);
  }, [deployments, guidedFocus, publication]);
  const selectedModel = model;
  const selectedModelInfo = models.find((item) => item.id === model);
  const selectedReasoningInfo = reasoningLevels.find((item) => item.id === reasoningEffort);
  const activeDeployment = deployments.find((deployment) => deployment.active) || null;
  const activeDeploymentVersion = activeDeployment?.build_time || activeDeployment?.created_at || activeDeployment?.version || "";
  const selectedUsage = usage?.projects?.find((item) => item.project_id === selectedId) || null;
  const hasPromptSubmissionContent = Boolean(prompt.trim() || attachments.length > 0);
  const livePreviewUrl = selected?.domain
    ? `https://${selected.domain}${activeDeploymentVersion || previewRefreshToken ? `?axqi_version=${encodeURIComponent(activeDeploymentVersion || previewRefreshToken)}` : ""}`
    : draftPreviewUrl;
  const previewUsesProduction = Boolean(selected?.domain);
  const visibleProjects = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((project) =>
      `${project.name} ${project.domain || ""} ${project.stack || ""}`.toLowerCase().includes(term)
    );
  }, [projects, query]);
  const promptQueueItems = useMemo(() => buildPromptQueue(promptQueueRuns), [promptQueueRuns]);
  const promptQueueStatus = useMemo(() => promptQueueSummary(promptQueueRuns), [promptQueueRuns]);
  const promptQueuePollInterval = useMemo(
    () => promptQueueRefreshInterval(promptQueueRuns, { hasActiveActionJobs }),
    [promptQueueRuns, hasActiveActionJobs]
  );
  const visiblePromptQueueItems = promptQueueItems.slice(0, 3);
  const hiddenPromptQueueCount = Math.max(0, promptQueueItems.length - visiblePromptQueueItems.length);
  const flatTree = useMemo(() => flattenTree(tree), [tree]);
  const hasCurrentChatTranscript = chatTranscriptProjectId === selectedId;
  const chatItems = useMemo(
    () => (hasCurrentChatTranscript ? chatTranscript : []),
    [chatTranscript, hasCurrentChatTranscript]
  );
  const isChatTranscriptRestoring = Boolean(selectedId && !hasCurrentChatTranscript && !chatTranscriptReady);
  const shellClassName = [
    "shell",
    sidebarCollapsed ? "sidebar-collapsed" : "",
    chatCollapsed ? "chat-collapsed" : "",
    activeTab === "usage" ? "wide-stage" : ""
  ].filter(Boolean).join(" ");
  const showPreviewStageControls = ["preview", "logs"].includes(activeTab);

  function clearPromptQueuePulses() {
    for (const timer of promptQueuePulseTimersRef.current) {
      window.clearTimeout(timer);
    }
    promptQueuePulseTimersRef.current = [];
  }

  function shouldPulsePromptQueue(promptRuns = []) {
    return promptQueueSummary(promptRuns).queuedCount > 0;
  }

  function schedulePromptQueuePulse(id, promptRuns = []) {
    clearPromptQueuePulses();
    if (!id || !shouldPulsePromptQueue(promptRuns)) return;
    const delays = [300, 900];
    promptQueuePulseTimersRef.current = delays.map((delay) =>
      window.setTimeout(() => {
        if (selectedIdRef.current !== id) return;
        refreshPromptQueue(id, { fromQueuePulse: true });
      }, delay)
    );
  }

  function syncPromptRuns(id, promptRuns = [], options = {}) {
    if (selectedIdRef.current !== id) return null;
    setRuns(promptRuns);
    setPromptQueueRuns(promptRuns);
    if (!options.fromQueuePulse) schedulePromptQueuePulse(id, promptRuns);
    const active = resolvePrimaryPromptRun(promptRuns);
    if (active) {
      setLog(active.progress_text || `Execução #${active.id} em andamento.`);
    }
    return active;
  }

  useEffect(() => {
    if (!guidedFocus) return;
    if (guidedFocus.tab !== activeTab) return;
    const timer = window.setTimeout(() => {
      if (guidedFocus.tab === "data") {
        if (guidedFocus.section === "gates" && focusedGateKey) {
          const gate = document.querySelector(`.infra-detail [data-gate-key="${focusedGateKey}"]`);
          gate?.scrollIntoView({ block: "nearest", behavior: "smooth" });
          return;
        }
        if (guidedFocus.section === "deployments" && focusedDeploymentId) {
          const deployment = document.querySelector(`.infra-detail [data-deployment-id="${focusedDeploymentId}"]`);
          deployment?.scrollIntoView({ block: "nearest", behavior: "smooth" });
          return;
        }
        const section = document.querySelector(`.infra-detail [data-section="${guidedFocus.section}"]`);
        section?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        return;
      }
      if (guidedFocus.tab === "logs") {
        if (focusedActivityId) {
          const activityItem = document.querySelector(`.activity-item[data-activity-id="${focusedActivityId}"]`);
          activityItem?.scrollIntoView({ block: "nearest", behavior: "smooth" });
          return;
        }
        if (focusedDeploymentId) {
          const deployment = document.querySelector(`.log-panel .release-history [data-deployment-id="${focusedDeploymentId}"]`);
          deployment?.scrollIntoView({ block: "nearest", behavior: "smooth" });
          return;
        }
        const activityItem = document.querySelector(".activity-item");
        activityItem?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }, 80);
    return () => window.clearTimeout(timer);
  }, [activeTab, focusedActivityId, focusedDeploymentId, focusedGateKey, guidedFocus, publication]);

  useEffect(() => {
    setGuidedFocus(null);
    setChatTranscript([]);
    setChatTranscriptProjectId("");
    setChatTranscriptReady(false);
  }, [selectedId]);

  useEffect(() => {
    if (!showPublish || !focusedDeploymentId) return;
    const timer = window.setTimeout(() => {
      const deployment = document.querySelector(`.publish-modal [data-deployment-id="${focusedDeploymentId}"]`);
      deployment?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [focusedDeploymentId, showPublish]);

  function formatDate(value) {
    if (!value) return "sem data";
    return new Date(value).toLocaleString();
  }

  function formatNumber(value) {
    return Number(value || 0).toLocaleString("pt-BR");
  }

  const previewSurface = (
    <div className={["preview-frame", `viewport-${previewDevice}`, visualEditMode ? "visual-editing" : ""].filter(Boolean).join(" ")}>
      {isSelfEditorPreview ? (
        <div className="empty-preview self-preview">
          <AppWindow size={30} />
          <strong>Editor selecionado</strong>
          <span>O preview embutido do próprio editor foi ocultado para evitar tela duplicada. Use as abas Código, Dados/Infra e Atividade, ou abra em nova aba.</span>
          <a href={`https://${selected.domain}`} target="_blank" rel="noreferrer"><Globe2 size={15} />Abrir editor</a>
        </div>
      ) : livePreviewUrl ? (
        <iframe
          title="preview atual"
          src={livePreviewUrl}
          sandbox="allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="empty-preview"><AppWindow size={30} />Projeto sem domínio publicado detectado.</div>
      )}
      {visualEditMode && (
        <div
          className="visual-edit-layer"
          role="button"
          tabIndex={0}
          aria-label="Selecionar área do preview"
          onPointerDown={beginVisualSelection}
          onPointerMove={updateVisualSelection}
          onPointerUp={finishVisualSelection}
          onPointerCancel={() => { visualDragRef.current = null; }}
        >
          <span>Arraste para selecionar uma área do preview</span>
          {visualSelection && (
            <i
              className="visual-selection-box"
              style={{
                left: `${visualSelection.left}%`,
                top: `${visualSelection.top}%`,
                width: `${visualSelection.width}%`,
                height: `${visualSelection.height}%`
              }}
            />
          )}
        </div>
      )}
    </div>
  );

  function promptPreviewText(value) {
    const text = String(value || "").replace(/\s+/g, " ").trim();
    return text.length > 170 ? `${text.slice(0, 170)}...` : text;
  }

  function formatChatTime(value) {
    if (!value) return "";
    return new Date(value).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  }

  async function checkSession() {
    try {
      const payload = await api("/api/auth/session");
      setAuth({ loading: false, authenticated: payload.authenticated, user: payload.user });
      if (payload.authenticated) {
        const preferences = await loadPreferences();
        loadProjects(preferences.selectedProjectId);
        api("/api/models").then((modelsPayload) => {
          setModels(modelsPayload.models || []);
          setReasoningLevels(modelsPayload.reasoningLevels || []);
          if (modelsPayload.defaultModel) setModel(modelsPayload.defaultModel);
          if (modelsPayload.defaultReasoningEffort) setReasoningEffort(modelsPayload.defaultReasoningEffort);
        }).catch(() => null);
        api("/api/settings").then(setSettings).catch(() => null);
      }
    } catch {
      setAuth({ loading: false, authenticated: false, user: null });
    }
  }

  async function login(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = await api("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(loginForm)
      });
      setAuth({ loading: false, authenticated: true, user: payload.user });
      const preferences = await loadPreferences();
      await loadProjects(preferences.selectedProjectId);
      api("/api/models").then((modelsPayload) => {
        setModels(modelsPayload.models || []);
        setReasoningLevels(modelsPayload.reasoningLevels || []);
        if (modelsPayload.defaultModel) setModel(modelsPayload.defaultModel);
        if (modelsPayload.defaultReasoningEffort) setReasoningEffort(modelsPayload.defaultReasoningEffort);
      }).catch(() => null);
      api("/api/settings").then(setSettings).catch(() => null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    await api("/api/auth/logout", { method: "POST" }).catch(() => null);
    setAuth({ loading: false, authenticated: false, user: null });
    setProjects([]);
    setSelectedId("");
    setPromptQueueRuns([]);
    setSettings(null);
  }

  async function loadPreferences() {
    try {
      const payload = await api("/api/preferences");
      const preferences = payload.preferences || {};
      preferencesReadyRef.current = false;
      setSelectedId(preferences.selectedProjectId || "");
      setPreviewDevice(preferences.previewDevice === "mobile" ? "mobile" : "desktop");
      setSidebarCollapsed(Boolean(preferences.sidebarCollapsed));
      setChatCollapsed(Boolean(preferences.chatCollapsed));
      preferencesReadyRef.current = true;
      return preferences;
    } catch {
      preferencesReadyRef.current = true;
      return {};
    }
  }

  async function savePreferences(partial) {
    if (!auth.authenticated || !preferencesReadyRef.current) return;
    await api("/api/preferences", {
      method: "PATCH",
      body: JSON.stringify(partial)
    }).catch(() => null);
  }

  async function loadProjects(preferredId = "") {
    setBusy(true);
    setError("");
    try {
      const payload = await api("/api/projects?published=1&fast=1");
      setProjects(payload.projects);
      setSelectedId((current) => {
        const candidate = current || preferredId;
        if (payload.projects.some((project) => project.id === candidate)) return candidate;
        return payload.projects[0]?.id || "";
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function loadProjectDetails(id, options = {}) {
    if (!id) return;
    setError("");
    try {
      const promptPayload = await api(`/api/projects/${id}/prompts`);
      syncPromptRuns(id, promptPayload.runs || [], options);
      const [treePayload, diffPayload, actionsPayload, deploymentsPayload, activityPayload, usagePayload] = await Promise.all([
        api(`/api/projects/${id}/tree`),
        api(`/api/projects/${id}/diff`),
        api(`/api/projects/${id}/actions`),
        api(`/api/projects/${id}/deployments`),
        api(`/api/projects/${id}/activity`),
        api("/api/usage")
      ]);
      if (selectedIdRef.current !== id) return;
      setTree(treePayload.tree);
      setActionEvents(actionsPayload.events);
      setActionJobs(actionsPayload.jobs || []);
      setDeployments(deploymentsPayload.deployments);
      setActivity(activityPayload.activity);
      setUsage(usagePayload.usage);
      setDiff(diffPayload.stdout || "");
      const active = resolvePrimaryPromptRun(promptPayload.runs);
      const activeJob = (actionsPayload.jobs || []).find((job) => ["queued", "running"].includes(job.status));
      const finishedPreview = (actionsPayload.jobs || []).find((job) => job.action === "preview" && job.status === "completed" && job.result?.previewUrl);
      if (active) {
        setLog(active.progress_text || `Execução #${active.id} em andamento.`);
      } else if (activeJob) {
        setLog(activeJob.progress_text || `Operação #${activeJob.id} em andamento.`);
      } else if (promptPayload.runs[0]?.result) {
        setLog((current) => current || promptPayload.runs[0].result);
      }
      if (finishedPreview) setDraftPreviewUrl(`${finishedPreview.result.previewUrl}?t=${Date.now()}`);
      setFilePath("");
      setFileContent("");
      if (!finishedPreview) {
        setDraftPreviewUrl("");
      }
      const infraPayload = await api(`/api/projects/${id}/infra`);
      if (selectedIdRef.current !== id) return;
      setInfra(infraPayload.infra);
      const transcriptPayload = await api(`/api/projects/${id}/transcript`);
      if (selectedIdRef.current !== id) return;
      setChatTranscript(transcriptPayload.items || []);
      setChatTranscriptProjectId(id);
      setChatTranscriptReady(true);
    } catch (err) {
      setError(err.message);
    }
  }

  async function refreshRuntime(id = selectedId, options = {}) {
    if (!id) return;
    try {
      const promptPayload = await api(`/api/projects/${id}/prompts`);
      syncPromptRuns(id, promptPayload.runs || [], options);
      const [diffPayload, actionsPayload, treePayload, deploymentsPayload, activityPayload, usagePayload] = await Promise.all([
        api(`/api/projects/${id}/diff`),
        api(`/api/projects/${id}/actions`),
        api(`/api/projects/${id}/tree`),
        api(`/api/projects/${id}/deployments`),
        api(`/api/projects/${id}/activity`),
        api("/api/usage")
      ]);
      if (selectedIdRef.current !== id) return;
      setDiff(diffPayload.stdout || "");
      setActionEvents(actionsPayload.events);
      setActionJobs(actionsPayload.jobs || []);
      setTree(treePayload.tree);
      setDeployments(deploymentsPayload.deployments);
      setActivity(activityPayload.activity);
      setUsage(usagePayload.usage);
      const active = resolvePrimaryPromptRun(promptPayload.runs);
      const activeJob = (actionsPayload.jobs || []).find((job) => ["queued", "running"].includes(job.status));
      if (active) {
        setLog(active.progress_text || `Execução #${active.id} em andamento.`);
      } else if (activeJob) {
        setLog(activeJob.progress_text || `Operação #${activeJob.id} em andamento.`);
      } else if (!activeJob) {
        const latest = promptPayload.runs[0];
        if (latest?.result) setLog(latest.result);
      }
      const finishedPreview = (actionsPayload.jobs || []).find((job) => job.action === "preview" && job.status === "completed" && job.result?.previewUrl);
      if (finishedPreview) {
        setDraftPreviewUrl(`${finishedPreview.result.previewUrl}?t=${Date.now()}`);
      }
      const infraPayload = await api(`/api/projects/${id}/infra`);
      if (selectedIdRef.current !== id) return;
      setInfra(infraPayload.infra);
      const transcriptPayload = await api(`/api/projects/${id}/transcript`);
      if (selectedIdRef.current !== id) return;
      setChatTranscript(transcriptPayload.items || []);
      setChatTranscriptProjectId(id);
      setChatTranscriptReady(true);
    } catch (err) {
      setError(err.message);
    }
  }

  async function openFile(path) {
    if (!selected) return;
    setError("");
    try {
      const payload = await api(`/api/projects/${selected.id}/file?path=${encodeURIComponent(path)}`);
      setFilePath(payload.path);
      setFileContent(payload.content);
    } catch (err) {
      setError(err.message);
    }
  }

  async function saveFile() {
    if (!selected || !filePath) return;
    const projectId = selected.id;
    setBusy(true);
    setError("");
    try {
      const payload = await api(`/api/projects/${projectId}/file`, {
        method: "PUT",
        body: JSON.stringify({ path: filePath, content: fileContent })
      });
      setChatTranscript((current) => mergeTranscriptItems(current, buildManualActionEventTranscriptEntries({
        action: "save_file",
        ok: payload.ok !== false,
        message: payload.message || "Arquivo salvo e histórico registrado.",
        createdAt: new Date().toISOString()
      })));
      setChatTranscriptProjectId(projectId);
      setChatTranscriptReady(true);
      const commitLine = payload.commitSha
        ? `Commit ${payload.commitSha.slice(0, 8)}${payload.pushed ? " enviado." : " registrado."}`
        : "Nenhuma alteração nova para registrar.";
      setLog(`${payload.message || "Arquivo salvo e histórico registrado."}\n${commitLine}`);
      void (async () => {
        try {
          const [diffPayload, actionsPayload, activityPayload, transcriptPayload] = await Promise.all([
            api(`/api/projects/${projectId}/diff`),
            api(`/api/projects/${projectId}/actions`),
            api(`/api/projects/${projectId}/activity`),
            api(`/api/projects/${projectId}/transcript`)
          ]);
          if (selectedIdRef.current !== projectId) return;
          setDiff(diffPayload.stdout || "");
          setActionEvents(actionsPayload.events);
          setActionJobs(actionsPayload.jobs || []);
          setActivity(activityPayload.activity);
          setChatTranscript(transcriptPayload.items || []);
          setChatTranscriptProjectId(projectId);
          setChatTranscriptReady(true);
        } catch (err) {
          if (selectedIdRef.current === projectId) {
            setError(err.message);
          }
        }
      })();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function refreshPromptQueue(id = selectedId, options = {}) {
    if (!id) return;
    try {
      const promptPayload = await api(`/api/projects/${id}/prompts`);
      syncPromptRuns(id, promptPayload.runs || [], options);
    } catch {
      // Keep the last known queue state if the standalone refresh fails.
    }
  }

  async function submitPrompt(promptText) {
    if (!selected || !selectedModel || composerSubmitting || promptSubmitPendingRef.current) return false;
    promptSubmitPendingRef.current = true;
    setComposerSubmitting(true);
    setError("");
    try {
      setActiveTab("preview");
      setChatTranscriptReady(false);
      const payload = await api(`/api/projects/${selected.id}/prompts`, {
        method: "POST",
        body: JSON.stringify({ prompt: promptText, mode, model: selectedModel, reasoningEffort, attachments })
      });
      setPrompt("");
      setAttachments([]);
      setRuns((current) => [payload.run, ...current]);
      setPromptQueueRuns((current) => [payload.run, ...current.filter((item) => item.id !== payload.run.id)]);
      setLog(payload.run.progress_text || "Execução enviada para o Codex.");
      setChatCollapsed(false);
      schedulePromptQueuePulse(selected.id, [payload.run]);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      promptSubmitPendingRef.current = false;
      setComposerSubmitting(false);
    }
  }

  function getPromptSubmissionPayload() {
    if (!selected || !selectedModel || composerSubmitting || promptSubmitPendingRef.current || !hasPromptSubmissionContent) return null;
    return {
      promptText: prompt.trim() || "Analise as imagens anexadas e use-as como referencia visual para OCR, copiar layout ou planejar a alteracao."
    };
  }

  function canSubmitCurrentPrompt() {
    return Boolean(getPromptSubmissionPayload());
  }

  function requestPromptSubmit() {
    const payload = getPromptSubmissionPayload();
    if (!payload) return false;
    return submitPrompt(payload.promptText);
  }

  function isChatComposerComposing(event) {
    return Boolean(
      event.isComposing ||
      event.nativeEvent?.isComposing ||
      chatInputComposingRef.current ||
      event.key === "Process" ||
      event.keyCode === 229 ||
      event.which === 229 ||
      event.nativeEvent?.keyCode === 229 ||
      event.nativeEvent?.which === 229
    );
  }

  function shouldSubmitPromptOnEnter(event) {
    if (event.key !== "Enter" || event.shiftKey) return false;
    if (event.altKey || event.ctrlKey || event.metaKey || event.defaultPrevented) return false;
    if (isChatComposerComposing(event)) return false;
    return true;
  }

  function trySubmitPromptFromKeyboard(event) {
    if (!shouldSubmitPromptOnEnter(event)) return false;
    event.preventDefault();
    return requestPromptSubmit();
  }

  function handlePromptKeyDown(event) {
    return trySubmitPromptFromKeyboard(event);
  }

  function handleChatComposerCompositionStart() {
    chatInputComposingRef.current = true;
  }

  function handleChatComposerCompositionEnd() {
    chatInputComposingRef.current = false;
  }

  async function approveRun(run) {
    if (!selected) return;
    const pendingRun = {
      id: `pending-approve-${run.id}`,
      project_id: selected.id,
      prompt: "Executando plano aprovado...",
      result: "",
      status: "queued",
      mode: "edit",
      model: selectedModel || run.model,
      reasoning_effort: reasoningEffort || run.reasoning_effort || "medium",
      attachments: [],
      approved_from_id: run.id,
      progress_text: "Plano aprovado. Iniciando execução agora...",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      optimistic: true
    };
    setBusy(true);
    setError("");
    setActiveTab("preview");
    setChatCollapsed(false);
    setChatTranscriptReady(false);
    setRuns((current) => [pendingRun, ...current.filter((item) => item.id !== pendingRun.id)]);
    setPromptQueueRuns((current) => [pendingRun, ...current.filter((item) => item.id !== pendingRun.id)]);
    setLog("Plano aprovado. Iniciando execução agora...");
    try {
      const payload = await api(`/api/projects/${selected.id}/prompts/${run.id}/approve`, {
        method: "POST",
        body: JSON.stringify({ model: selectedModel, reasoningEffort })
      });
      setRuns((current) => [payload.run, ...current.filter((item) => item.id !== pendingRun.id && item.id !== payload.run.id)]);
      setPromptQueueRuns((current) => [payload.run, ...current.filter((item) => item.id !== pendingRun.id && item.id !== payload.run.id)]);
      setLog(payload.run.progress_text || "Edição enviada para o Codex.");
      schedulePromptQueuePulse(selected.id, [payload.run]);
    } catch (err) {
      setRuns((current) => current.filter((item) => item.id !== pendingRun.id));
      setPromptQueueRuns((current) => current.filter((item) => item.id !== pendingRun.id));
      setError(err.message);
      setLog("");
    } finally {
      setBusy(false);
    }
  }

  async function revertRun(run) {
    if (!selected || !confirm(`Reverter alterações da execução #${run.id}?`)) return;
    const projectId = selected.id;
    setBusy(true);
    setError("");
    try {
      const payload = await api(`/api/projects/${projectId}/prompts/${run.id}/revert`, { method: "POST" });
      setLog(payload.message || "Alteração revertida.");
      setChatTranscript((current) => mergeTranscriptItems(current, buildManualActionEventTranscriptEntries({
        action: "revert",
        ok: payload.ok !== false,
        message: payload.message || "Alteração revertida.",
        createdAt: new Date().toISOString()
      })));
      setChatTranscriptProjectId(projectId);
      setChatTranscriptReady(true);
      setActiveTab("preview");
      void (async () => {
        try {
          const [diffPayload, actionsPayload, activityPayload, transcriptPayload] = await Promise.all([
            api(`/api/projects/${projectId}/diff`),
            api(`/api/projects/${projectId}/actions`),
            api(`/api/projects/${projectId}/activity`),
            api(`/api/projects/${projectId}/transcript`)
          ]);
          if (selectedIdRef.current !== projectId) return;
          setDiff(diffPayload.stdout || "");
          setActionEvents(actionsPayload.events);
          setActionJobs(actionsPayload.jobs || []);
          setActivity(activityPayload.activity);
          setChatTranscript(transcriptPayload.items || []);
          setChatTranscriptProjectId(projectId);
          setChatTranscriptReady(true);
        } catch (err) {
          if (selectedIdRef.current === projectId) {
            setError(err.message);
          }
        }
      })();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function cancelRun(run) {
    if (!selected) return;
    setError("");
    try {
      setChatTranscriptReady(false);
      const payload = await api(`/api/projects/${selected.id}/prompts/${run.id}/cancel`, { method: "POST" });
      setRuns((current) => current.map((item) => item.id === run.id ? payload.run : item));
      setPromptQueueRuns((current) => current.map((item) => item.id === run.id ? payload.run : item));
      setLog(payload.message || "Cancelamento solicitado.");
      schedulePromptQueuePulse(selected.id, runs);
      void refreshRuntime(selected.id);
    } catch (err) {
      setError(err.message);
    }
  }

  async function cancelActionJob(job) {
    if (!selected) return;
    setError("");
    try {
      setChatTranscriptReady(false);
      const payload = await api(`/api/projects/${selected.id}/actions/${job.id}/cancel`, { method: "POST" });
      setActionJobs((current) => current.map((item) => item.id === job.id ? payload.job : item));
      setChatTranscript((current) => mergeTranscriptItems(current, buildActionJobTranscriptEntries(payload.job)));
      setChatTranscriptProjectId(selected.id);
      setChatTranscriptReady(true);
      setLog(payload.message || "Cancelamento solicitado.");
      void refreshRuntime(selected.id);
    } catch (err) {
      setError(err.message);
    }
  }

  function addFiles(files) {
    const incoming = [...files];
    const allowedImageTypes = new Set(["image/png", "image/jpeg", "image/jpg", "image/webp"]);
    const images = incoming.filter((file) => allowedImageTypes.has(file.type));
    const slots = Math.max(0, 6 - attachments.length);
    const selectedImages = images.slice(0, slots);

    if (!incoming.length) return;
    if (!images.length) {
      setAttachmentNotice("Use imagens PNG, JPG, WebP ou JPEG.");
      return;
    }
    if (!slots) {
      setAttachmentNotice("Limite de 6 imagens por mensagem.");
      return;
    }
    if (images.length > selectedImages.length || incoming.length > images.length) {
      setAttachmentNotice(`Foram adicionadas ${selectedImages.length} imagem(ns). O limite por mensagem é 6.`);
    } else {
      setAttachmentNotice(`${selectedImages.length} imagem(ns) pronta(s). Diga se quer ler texto, copiar layout ou ajustar a tela.`);
    }

    selectedImages.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments((current) => [
          ...current,
          { id: crypto.randomUUID(), name: file.name || "imagem", type: file.type, dataUrl: reader.result }
        ].slice(0, 6));
      };
      reader.readAsDataURL(file);
    });
  }

  function onPaste(event) {
    const files = [...event.clipboardData.files].filter((file) => file.type.startsWith("image/"));
    if (files.length) {
      event.preventDefault();
      addFiles(files);
    }
  }

  function onDrop(event) {
    event.preventDefault();
    addFiles(event.dataTransfer.files);
  }

  function visualPointFromEvent(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100))
    };
  }

  function normalizeVisualSelection(start, end) {
    const left = Math.min(start.x, end.x);
    const top = Math.min(start.y, end.y);
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);
    if (width < 1.5 && height < 1.5) {
      return {
        left: Math.max(0, start.x - 7),
        top: Math.max(0, start.y - 5),
        width: Math.min(14, 100 - Math.max(0, start.x - 7)),
        height: Math.min(10, 100 - Math.max(0, start.y - 5))
      };
    }
    return {
      left,
      top,
      width: Math.max(2, width),
      height: Math.max(2, height)
    };
  }

  function describeVisualSelection(selection) {
    return [
      "Edição visual:",
      `- Ajustar a área selecionada no preview: esquerda ${selection.left.toFixed(1)}%, topo ${selection.top.toFixed(1)}%, largura ${selection.width.toFixed(1)}%, altura ${selection.height.toFixed(1)}%.`,
      `- Centro aproximado: ${(selection.left + selection.width / 2).toFixed(1)}% da largura e ${(selection.top + selection.height / 2).toFixed(1)}% da altura.`,
      `- Preview alvo: ${selection.target}.`,
      "- Use a seleção visual como referência de layout; preserve o restante da tela."
    ].join("\n");
  }

  function clearVisualSelection() {
    setVisualSelection(null);
    visualDragRef.current = null;
    setPrompt((current) => current.replace(visualSelectionPattern, "").trim());
  }

  function beginVisualSelection(event) {
    if (!visualEditMode) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const point = visualPointFromEvent(event);
    visualDragRef.current = { pointerId: event.pointerId, start: point };
    setVisualSelection({
      ...normalizeVisualSelection(point, point),
      dragging: true,
      mode: previewUsesProduction ? "production" : "automatic-preview",
      target: previewUsesProduction ? selected.domain : "preview automatico atualizado"
    });
  }

  function updateVisualSelection(event) {
    const drag = visualDragRef.current;
    if (!visualEditMode || !drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    const selection = normalizeVisualSelection(drag.start, visualPointFromEvent(event));
    setVisualSelection({
      ...selection,
      dragging: true,
      mode: previewUsesProduction ? "production" : "automatic-preview",
      target: previewUsesProduction ? selected.domain : "preview automatico atualizado"
    });
  }

  function finishVisualSelection(event) {
    const drag = visualDragRef.current;
    if (!visualEditMode || !drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    visualDragRef.current = null;
    const selection = {
      ...normalizeVisualSelection(drag.start, visualPointFromEvent(event)),
      dragging: false,
      mode: previewUsesProduction ? "production" : "automatic-preview",
      target: previewUsesProduction ? selected.domain : "preview automatico atualizado"
    };
    setVisualSelection(selection);
    setMode("edit");
    setPrompt((current) => {
      const note = describeVisualSelection(selection);
      const withoutPreviousSelection = current.replace(visualSelectionPattern, "").trim();
      return withoutPreviousSelection ? `${withoutPreviousSelection}\n\n${note}` : note;
    });
  }

  async function runAction(action) {
    if (!selected) return;
    const projectId = selected.id;
    setBusy(true);
    setError("");
    setLog(`Enviando ${action} para segundo plano...`);
    try {
      if (["build", "preview", "deploy"].includes(action)) setActiveTab("preview");
      setChatTranscriptReady(false);
      const payload = await api(`/api/projects/${projectId}/actions`, {
        method: "POST",
        body: JSON.stringify({ action })
      });
      if (payload.job) {
        setActionJobs((current) => [payload.job, ...current.filter((job) => job.id !== payload.job.id)]);
        setChatTranscript((current) => mergeTranscriptItems(current, buildActionJobTranscriptEntries(payload.job)));
        setChatTranscriptProjectId(projectId);
        setChatTranscriptReady(true);
      }
      setLog(payload.job?.progress_text || `${action} entrou na fila.`);
      setChatCollapsed(false);
      void (async () => {
        try {
          const [actionsPayload, deploymentsPayload, activityPayload, transcriptPayload] = await Promise.all([
            api(`/api/projects/${projectId}/actions`),
            api(`/api/projects/${projectId}/deployments`),
            api(`/api/projects/${projectId}/activity`),
            api(`/api/projects/${projectId}/transcript`)
          ]);
          if (selectedIdRef.current !== projectId) return;
          setActionEvents(actionsPayload.events);
          setActionJobs(actionsPayload.jobs || []);
          setDeployments(deploymentsPayload.deployments);
          setActivity(activityPayload.activity);
          setChatTranscript(transcriptPayload.items || []);
          setChatTranscriptProjectId(projectId);
          setChatTranscriptReady(true);
        } catch (err) {
          if (selectedIdRef.current === projectId) {
            setError(err.message);
          }
        }
      })();
    } catch (err) {
      setError(err.message);
      setLog("");
    } finally {
      setBusy(false);
    }
  }

  async function rollbackDeployment(deployment) {
    const label = deployment.version || formatDate(deployment.build_time || deployment.created_at);
    if (!selected || !confirm(`Publicar novamente a versão ${label}?`)) return;
    const projectId = selected.id;
    setBusy(true);
    setError("");
    try {
      const payload = await api(`/api/projects/${projectId}/deployments/${deployment.id}/rollback`, { method: "POST" });
      setChatTranscript((current) => mergeTranscriptItems(current, buildManualActionEventTranscriptEntries({
        action: "rollback_release",
        ok: payload.ok !== false,
        message: payload.message || (payload.ok === false ? "Rollback precisa de revisão." : "Versão anterior republicada."),
        createdAt: new Date().toISOString()
      })));
      setChatTranscriptProjectId(projectId);
      setChatTranscriptReady(true);
      setLog(payload.message || "Versão republicada.");
      void refreshRuntime(projectId);
    } catch (err) {
      setError(err.message);
      setLog("");
    } finally {
      setBusy(false);
    }
  }

  function runPublicationUiAction(actionKind) {
    if (!selected) return;
    if (actionKind === "validate") {
      void runProjectValidationAction();
      return;
    }
    if (actionKind === "publish" || actionKind === "published") {
      setShowPublish(true);
      return;
    }
    if (actionKind === "logs") {
      setActiveTab("logs");
      setChatCollapsed(false);
      setGuidedFocus(guidedFocusTarget(publication, "logs"));
      return;
    }
    if (actionKind === "review") {
      setActiveTab("data");
      setChatCollapsed(false);
      setGuidedFocus(guidedFocusTarget(publication, "review"));
      return;
    }
    if (actionKind === "gates") {
      setActiveTab("data");
      setChatCollapsed(false);
      setGuidedFocus(guidedFocusTarget(publication, "gates"));
      return;
    }
    if (actionKind === "publication") {
      setActiveTab("data");
      setChatCollapsed(false);
      setGuidedFocus(guidedFocusTarget(publication, "publication"));
      return;
    }
    runAction("status");
  }

  function focusPublicationRisk(risk, options = {}) {
    const target = riskTargetFromPublicationRisk(risk);
    if (!target) return;
    if (options.closePublish) setShowPublish(false);
    if (options.closeProjectConfig) setShowProjectConfig(false);
    setGuidedFocus(target);
    setActiveTab(target.tab);
    setChatCollapsed(false);
  }

  function openPublicationPrimaryContext(options = {}) {
    if (!publication) return;
    if (publication.nextAction === "check_logs" && publication.activityRef) {
      focusPublicationRisk({ source: "activity", targetSection: "activity", activityRef: publication.activityRef }, options);
      return;
    }
    if (publication.gateRef) {
      focusPublicationRisk({ source: "gate", targetSection: publication.targetSection || "gates", gateRef: publication.gateRef }, options);
      return;
    }
    if (publication.deploymentRef) {
      focusPublicationRisk({ source: "publication", targetSection: publication.targetSection || "deployments", deploymentRef: publication.deploymentRef }, options);
      return;
    }
    if (publication.nextAction === "publish") {
      if (options.closeProjectConfig) setShowProjectConfig(false);
      setShowPublish(true);
      return;
    }
    setGuidedFocus({ tab: publication.targetTab || "data", section: publication.targetSection || "summary" });
    if (options.closeProjectConfig) setShowProjectConfig(false);
    setActiveTab(publication.targetTab || "data");
    setChatCollapsed(false);
  }

  async function runProjectValidationAction(options = {}) {
    if (options.closePublish) setShowPublish(false);
    if (options.closeProjectConfig) setShowProjectConfig(false);
    await runAction("test");
  }

  async function confirmPublicationModalAction() {
    if (modalPublicationAction.kind === "publish") {
      setShowPublish(false);
      await runAction("deploy");
      return;
    }
    if (modalPublicationAction.kind === "logs") {
      setShowPublish(false);
      runPublicationUiAction("logs");
      return;
    }
    if (modalPublicationAction.kind === "review") {
      setShowPublish(false);
      runPublicationUiAction("review");
    }
  }

  function openProjectConfigDrawer() {
    setConfigDraft(profileDraftFromWorkflow(infra?.workflow));
    setShowProjectConfig(true);
  }

  async function saveProjectConfig() {
    if (!selected || !configDraft) return;
    setSavingConfig(true);
    setError("");
    try {
      await api(`/api/projects/${selected.id}/config`, {
        method: "PATCH",
        body: JSON.stringify({
          previewStrategy: configDraft.previewStrategy,
          publishStrategy: configDraft.publishStrategy,
          dbKind: configDraft.dbKind,
          migrationCmd: configDraft.migrationCmd.trim() || null,
          seedCmd: configDraft.seedCmd.trim() || null,
          dbContainer: configDraft.dbContainer.trim() || null,
          runtimeServices: configDraft.runtimeServicesText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean),
          validationCmds: configDraft.validationCmdsText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean),
          notes: configDraft.notes.trim() || null
        })
      });
      setLog("Configuração do projeto atualizada.");
      await refreshRuntime(selected.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingConfig(false);
    }
  }

  function openCreateProject() {
    setCreateProjectDraft({ name: "", slug: "", prompt: "", slugTouched: false });
    setShowCreateProject(true);
  }

  function openImportProject() {
    setImportProjectDraft({ name: "", slug: "", repositoryUrl: "", prompt: "", slugTouched: false });
    setShowImportProject(true);
  }

  function updateCreateProjectName(value) {
    setCreateProjectDraft((current) => ({
      ...current,
      name: value,
      slug: current.slugTouched ? current.slug : slugifyProjectName(value)
    }));
  }

  function updateCreateProjectSlug(value) {
    setCreateProjectDraft((current) => ({
      ...current,
      slug: slugifyProjectName(value),
      slugTouched: true
    }));
  }

  function updateImportProjectName(value) {
    setImportProjectDraft((current) => ({
      ...current,
      name: value,
      slug: current.slugTouched ? current.slug : slugifyProjectName(value)
    }));
  }

  function updateImportProjectSlug(value) {
    setImportProjectDraft((current) => ({
      ...current,
      slug: slugifyProjectName(value),
      slugTouched: true
    }));
  }

  async function createProject() {
    const name = createProjectDraft.name.trim();
    const slug = createProjectDraft.slug.trim();
    const initialPrompt = createProjectDraft.prompt.trim();
    if (!name || !slug || !initialPrompt) {
      setError("Preencha nome, subdomínio e prompt inicial do novo projeto.");
      return;
    }
    setCreatingProject(true);
    setBusy(true);
    setError("");
    setLog(`Provisionando ${slug}.axqi.ai, repositório privado e base inicial do projeto.`);
    try {
      const payload = await api("/api/projects/create", {
        method: "POST",
        body: JSON.stringify({ name, slug, initialPrompt })
      });
      await loadProjects(payload.project?.id || "");
      if (payload.project?.id) {
        setSelectedId(payload.project.id);
      }
      setLog(`Projeto ${slug}.axqi.ai criado. O prompt inicial já entrou na fila do Codex.`);
      if (Array.isArray(payload.warnings) && payload.warnings.length) {
        setError(payload.warnings.join(" "));
      }
      setShowCreateProject(false);
      setCreateProjectDraft({ name: "", slug: "", prompt: "", slugTouched: false });
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingProject(false);
      setBusy(false);
    }
  }

  async function importProject() {
    const name = importProjectDraft.name.trim();
    const slug = importProjectDraft.slug.trim();
    const repositoryUrl = importProjectDraft.repositoryUrl.trim();
    const initialPrompt = importProjectDraft.prompt.trim();
    if (!name || !slug || !repositoryUrl) {
      setError("Preencha nome, subdomínio e URL do GitHub para importar o projeto.");
      return;
    }
    setImportingProject(true);
    setBusy(true);
    setError("");
    setLog(`Importando ${repositoryUrl} para ${slug}.axqi.ai e configurando publicação automática.`);
    try {
      const payload = await api("/api/projects/import-github", {
        method: "POST",
        body: JSON.stringify({ name, slug, repositoryUrl, initialPrompt })
      });
      await loadProjects(payload.project?.id || "");
      if (payload.project?.id) {
        setSelectedId(payload.project.id);
      }
      setLog(initialPrompt
        ? `Projeto ${slug}.axqi.ai importado. O prompt inicial já entrou na fila do Codex.`
        : `Projeto ${slug}.axqi.ai importado e publicado a partir do GitHub.`);
      if (Array.isArray(payload.warnings) && payload.warnings.length) {
        setError(payload.warnings.join(" "));
      }
      setShowImportProject(false);
      setImportProjectDraft({ name: "", slug: "", repositoryUrl: "", prompt: "", slugTouched: false });
    } catch (err) {
      setError(err.message);
    } finally {
      setImportingProject(false);
      setBusy(false);
    }
  }

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (selectedId) savePreferences({ selectedProjectId: selectedId });
  }, [selectedId]);

  useEffect(() => {
    savePreferences({ previewDevice });
  }, [previewDevice]);

  useEffect(() => {
    savePreferences({ sidebarCollapsed });
  }, [sidebarCollapsed]);

  useEffect(() => {
    savePreferences({ chatCollapsed });
  }, [chatCollapsed]);

  useEffect(() => {
    const element = runsRef.current;
    if (!element) return;
    element.scrollTop = element.scrollHeight;
  }, [chatItems.length, activeRun?.progress_text, activeRun?.status, activeActionJob?.progress_text, activeActionJob?.status]);

  useEffect(() => {
    if (!selectedId) return;
    const latestCompletedPrompt = runs.find((run) => run.status === "completed" && (run.commit_sha || run.completed_at));
    const latestCompletedUpdate = actionJobs.find((job) =>
      ["build", "preview", "deploy"].includes(job.action) && job.status === "completed"
    );
    if (!latestCompletedPrompt && !latestCompletedUpdate) {
      setPreviewRefreshToken("");
      return;
    }
    const signature = [
      selectedId,
      latestCompletedPrompt?.id || "",
      latestCompletedPrompt?.commit_sha || latestCompletedPrompt?.completed_at || "",
      latestCompletedUpdate?.id || "",
      latestCompletedUpdate?.completed_at || latestCompletedUpdate?.updated_at || ""
    ].join(":");
    setPreviewRefreshToken(signature);
  }, [selectedId, runs, actionJobs]);

  useEffect(() => {
    const onExpired = () => {
      setAuth({ loading: false, authenticated: false, user: null });
      setError("Sessao expirada. Entre novamente para continuar.");
    };
    window.addEventListener("axqi-auth-expired", onExpired);
    return () => window.removeEventListener("axqi-auth-expired", onExpired);
  }, []);

  useEffect(() => {
    if (!auth.authenticated) return;
    loadProjectDetails(selectedId);
  }, [auth.authenticated, selectedId]);

  useEffect(() => {
    if (!auth.authenticated || !selectedId) return;
    refreshPromptQueue(selectedId);
  }, [auth.authenticated, selectedId]);

  useEffect(() => {
    if (!auth.authenticated || !selectedId || promptQueueStatus.total === 0) return undefined;
    const timer = setInterval(() => {
      refreshPromptQueue(selectedId);
    }, promptQueuePollInterval);
    return () => clearInterval(timer);
  }, [auth.authenticated, selectedId, promptQueuePollInterval, promptQueueStatus.total]);

  useEffect(() => {
    if (!auth.authenticated || !selectedId) return undefined;
    const timer = setInterval(() => {
      refreshRuntime(selectedId);
    }, activeRun?.status === "running" || hasActiveActionJobs ? 2500 : 8000);
    return () => clearInterval(timer);
  }, [auth.authenticated, selectedId, activeRun?.status, hasActiveActionJobs]);

  useEffect(() => () => clearPromptQueuePulses(), []);

  if (auth.loading) {
    return <main className="loading-screen">AXQI Editor</main>;
  }

  if (!auth.authenticated) {
    return (
      <main className="login-screen">
        <section className="login-visual">
          <div className="login-brand">
            <div className="brand-mark"><Bot size={22} /></div>
            <span>AXQI Editor</span>
          </div>
          <div className="login-copy">
            <h1>Editor interno para projetos, previews e deploys.</h1>
            <p>Ambiente privado para revisar código e publicar versões dos apps da empresa.</p>
          </div>
          <div className="login-metrics">
            <span><FolderGit2 size={17} />Projetos locais</span>
            <span><ShieldCheck size={17} />Acesso protegido</span>
            <span><Rocket size={17} />Deploy controlado</span>
          </div>
        </section>
        <section className="login-panel">
          <form onSubmit={login}>
            <div>
              <h2>Entrar</h2>
              <p>Acesse com as credenciais internas do editor.</p>
            </div>
            {error && <div className="notice">{error}</div>}
            <label>
              Usuario
              <input
                value={loginForm.username}
                onChange={(event) => setLoginForm((current) => ({ ...current, username: event.target.value }))}
                autoComplete="username"
              />
            </label>
            <label>
              Senha
              <input
                type="password"
                value={loginForm.password}
                onChange={(event) => setLoginForm((current) => ({ ...current, password: event.target.value }))}
                autoComplete="current-password"
              />
            </label>
            <button className="login-button" disabled={busy}>
              <ShieldCheck size={17} />Entrar no editor
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className={shellClassName}>
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark"><Bot size={18} /></div>
          <div>
            <strong>AXQI Editor</strong>
            <span>Projetos internos em produção</span>
          </div>
          <button className="sidebar-toggle" onClick={() => setSidebarCollapsed((current) => !current)} title={sidebarCollapsed ? "Abrir projetos" : "Recolher sidebar"}>
            {sidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        <label className="search">
          <Search size={16} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar projeto" />
        </label>

        <div className="sidebar-section">
          <div className="sidebar-section-head">
            <small className="sidebar-label">Projetos</small>
            <div className="sidebar-actions">
            <button className="sidebar-action" onClick={openImportProject} disabled={busy || importingProject} title="Importar projeto do GitHub">
              <Globe2 size={14} />Importar GitHub
            </button>
            <button className="sidebar-action" onClick={openCreateProject} disabled={busy || creatingProject} title="Criar novo projeto">
              <Plus size={14} />Novo projeto
            </button>
            </div>
          </div>
        <div className="project-list">
          {visibleProjects.map((project) => (
            <button
              key={project.id}
              className={project.id === selectedId ? "project active" : "project"}
              data-project-name={project.name}
              data-project-domain={project.domain || ""}
              title={`${project.name}${project.domain ? ` · ${project.domain}` : ""}`}
              onClick={() => {
                setSelectedId(project.id);
                if (window.matchMedia("(max-width: 900px)").matches) setSidebarCollapsed(true);
              }}
            >
              <FolderGit2 size={17} />
              <span>
                <strong>{project.name}</strong>
                <small>{project.domain || `${project.workspaceLabel} · ${project.stack}`}</small>
              </span>
            </button>
          ))}
        </div>
        </div>
      </aside>

      <section className="workspace">
        <div className="workspace-header">
          <header className="topbar">
            <div className="topbar-primary">
              <div className="topbar-path">
                <span className="topbar-url">{selected?.domain || selected?.name || "Carregando workspace"}</span>
              </div>
              <div className="topbar-summary">
                <div className="topbar-heading">
                  <h1>{selected?.name || "Editor"}</h1>
                  <p>{publication?.detail || (selected?.domain ? `Produção em ${selected.domain}` : "Projeto interno sem domínio publicado")}</p>
                </div>
                {publication && (
                  <div className={`publication-chip ${publicationStateTone}`} data-publication-state={publication.state}>
                    <strong>{publication.label}</strong>
                    {publicationRiskText && <small>{publicationRiskText}</small>}
                  </div>
                )}
              </div>
            </div>
            <div className="actions">
              <button onClick={logout}><ShieldCheck size={16} />{auth.user}</button>
              <button onClick={loadProjects} disabled={busy}><RefreshCcw size={16} />Atualizar</button>
              <button onClick={() => setChatCollapsed((current) => !current)} disabled={!selected}>
                {chatCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
                {chatCollapsed ? "Chat" : "Foco"}
              </button>
              <button onClick={() => runAction("status")} disabled={!selected || busy}>
                <Activity size={16} />Status
              </button>
              <div className="project-actions-toolbar">
                <button onClick={() => runPublicationUiAction(toolbarPublicationAction.kind)} disabled={!selected || busy}>
                  <Activity size={15} />{toolbarPublicationAction.label}
                </button>
                <button onClick={openProjectConfigDrawer} disabled={!selected}><Settings2 size={15} />Projeto</button>
              </div>
              <button
                className={primaryPublicationAction.kind === "publish" ? "danger" : ""}
                onClick={() => runPublicationUiAction(primaryPublicationAction.kind)}
                disabled={!selected || busy}
                title={primaryPublicationAction.title}
              >
                <Rocket size={16} />{primaryPublicationAction.label}
              </button>
            </div>
          </header>

          <div className="workspace-controlbar">
            <div className="stage-tabs">
              <button className={activeTab === "preview" ? "on" : ""} onClick={() => setActiveTab("preview")}><Eye size={16} />Preview</button>
              <button className={activeTab === "code" ? "on" : ""} onClick={() => setActiveTab("code")}><Code2 size={16} />Código</button>
              <button className={activeTab === "data" ? "on" : ""} onClick={() => setActiveTab("data")}><Database size={16} />Dados/Infra</button>
              <button className={activeTab === "usage" ? "on" : ""} onClick={() => setActiveTab("usage")}><Activity size={16} />Consumo</button>
              <button className={activeTab === "logs" ? "on" : ""} onClick={() => setActiveTab("logs")}><History size={16} />Atividade</button>
            </div>
            {showPreviewStageControls && (
              <div className="preview-toolbar">
                <span
                  className={`release-chip ${publicationStateTone === "error" ? "warn" : publicationStateTone}`}
                  title={publication?.detail || (activeDeployment ? `Versão ${activeDeployment.version || formatDate(activeDeployment.build_time || activeDeployment.created_at)}` : "Nenhuma versão publicada para este projeto")}
                >
                  <Rocket size={15} />
                  <span>
                    <strong>{publication?.label || (previewUsesProduction ? "Publicado" : "Atualizado")}</strong>
                    <small>
                      {publication?.detail || (activeDeployment
                        ? `${activeDeployment.source_sha ? activeDeployment.source_sha.slice(0, 8) : "sem sha"} · ${formatDate(activeDeployment.build_time || activeDeployment.created_at)}`
                        : "sem versão publicada")}
                    </small>
                  </span>
                </span>
                <div className="viewport-switch" aria-label="Modo do preview">
                  <button
                    className={previewDevice === "desktop" ? "on" : ""}
                    onClick={() => setPreviewDevice("desktop")}
                    title="Desktop"
                    aria-label="Desktop"
                    type="button"
                  >
                    <Monitor size={15} />Desktop
                  </button>
                  <button
                    className={previewDevice === "mobile" ? "on" : ""}
                    onClick={() => setPreviewDevice("mobile")}
                    title="Móvel"
                    aria-label="Móvel"
                    type="button"
                  >
                    <Smartphone size={15} />Móvel
                  </button>
                </div>
                <button className={visualEditMode ? "on" : ""} onClick={() => setVisualEditMode((current) => !current)} disabled={!selected}>
                  <MousePointer2 size={15} />Editar visual
                </button>
                {selected?.domain && (
                  <a href={`https://${selected.domain}`} target="_blank" rel="noreferrer"><Globe2 size={15} />Abrir</a>
                )}
              </div>
            )}
          </div>
        </div>

        {error && <div className="notice">{error}</div>}

        <div className="axqi-layout">
          <section className="panel prompt-panel builder-chat">
            <div className="panel-head">
              <span><Sparkles size={16} />Chat do projeto</span>
            </div>
            <textarea
              className="prompt chat-composer-input"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              onKeyDown={handlePromptKeyDown}
              enterKeyHint="send"
              onCompositionStart={handleChatComposerCompositionStart}
              onCompositionEnd={handleChatComposerCompositionEnd}
              onBlur={() => {
                chatInputComposingRef.current = false;
              }}
              onPaste={onPaste}
              onDrop={onDrop}
              onDragOver={(event) => event.preventDefault()}
              placeholder="Descreva a alteração, cole uma imagem ou peça para copiar um layout..."
              aria-label="Mensagem do chat do projeto"
              data-chat-composer="true"
            />
            {!!promptQueueItems.length && (
              <div className="prompt-queue-strip" aria-live="polite">
                <div className="prompt-queue-head">
                  <strong>Na fila</strong>
                  <span>{promptQueueStatus.text}</span>
                </div>
                <div className="prompt-queue-list">
                  {visiblePromptQueueItems.map((item) => {
                    const queueRun = promptQueueRuns.find((run) => String(run.id) === String(item.id));
                    return (
                      <div key={item.id} className={`prompt-queue-item ${item.status}`}>
                        <span className="prompt-queue-position">#{item.queuePosition}</span>
                        <span className="prompt-queue-mode">{item.modeLabel}</span>
                        <span className="prompt-queue-text" title={queueRun?.prompt || item.promptPreview}>
                          {item.promptPreview || "Prompt sem texto"}
                        </span>
                        <span className="prompt-queue-status">{item.statusLabel}</span>
                        <button
                          type="button"
                          className="prompt-queue-cancel"
                          onClick={() => queueRun && cancelRun(queueRun)}
                          disabled={!queueRun}
                          title={`Cancelar prompt #${item.queuePosition}`}
                        >
                          <XCircle size={13} />
                          Cancelar
                        </button>
                      </div>
                    );
                  })}
                  {hiddenPromptQueueCount > 0 && (
                    <span className="prompt-queue-more">+{hiddenPromptQueueCount} na fila</span>
                  )}
                </div>
              </div>
            )}
            <div className="attachments">
              <div className="composer-toolbar">
                <label className="attachment-add">
                  <Paperclip size={15} />Imagem
                  <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" multiple onChange={(event) => addFiles(event.target.files)} />
                </label>
                <span className="attachment-count">{attachments.length}/6</span>
                <details className="composer-advanced">
                  <summary>
                    <Settings2 size={15} />
                    Configuração
                    <span>{selectedModelInfo?.label || model} · {selectedReasoningInfo?.label || reasoningEffort}</span>
                  </summary>
                  <div className="composer-settings-popover">
                    <label className="composer-model" title={selectedModelInfo?.description || "Modelo usado pelo Codex"}>
                      <span>Modelo</span>
                      <select value={model} onChange={(event) => setModel(event.target.value)}>
                        {(models.length ? models : [{ id: "gpt-5.4", label: "GPT-5.4" }]).map((item) => (
                          <option key={item.id} value={item.id}>{item.label} · {item.id}</option>
                        ))}
                      </select>
                    </label>
                    <label className="composer-reasoning" title={selectedReasoningInfo?.description || "Nivel de reasoning"}>
                      <span>Reasoning</span>
                      <select value={reasoningEffort} onChange={(event) => setReasoningEffort(event.target.value)}>
                        {(reasoningLevels.length ? reasoningLevels : [
                          { id: "low", label: "Low" },
                          { id: "medium", label: "Medium" },
                          { id: "high", label: "High" },
                          { id: "xhigh", label: "XHigh" }
                        ]).map((item) => (
                          <option key={item.id} value={item.id}>{item.label}</option>
                        ))}
                      </select>
                    </label>
                    {settings && <span className="thread-count">{settings.codexMaxThreads} threads disponíveis</span>}
                  </div>
                </details>
                {visualSelection && (
                  <button className="visual-selection-chip" type="button" onClick={clearVisualSelection} title="Limpar seleção visual">
                    <MousePointer2 size={14} />
                    Área {Math.round(visualSelection.width)}×{Math.round(visualSelection.height)}%
                    <XCircle size={13} />
                  </button>
                )}
                <div className="composer-modes" aria-label="Modo do Codex">
                  <button type="button" className={mode === "plan" ? "on" : ""} onClick={() => setMode("plan")}>Plano</button>
                  <button type="button" className={mode === "edit" ? "on" : ""} onClick={() => setMode("edit")}>Editar</button>
                </div>
                <button className="send-button" type="button" onClick={requestPromptSubmit} disabled={!canSubmitCurrentPrompt()}>
                  <Bot size={15} />Enviar
                </button>
              </div>
              {attachmentNotice && <small className="attachment-notice">{attachmentNotice}</small>}
              {attachments.length > 0 && (
                <div className="attachment-list">
                  {attachments.map((attachment) => (
                    <button
                      key={attachment.id}
                      className="attachment-item"
                      title={`Remover ${attachment.name}`}
                      onClick={() => setAttachments((current) => current.filter((item) => item.id !== attachment.id))}
                    >
                      <img src={attachment.dataUrl} alt="" />
                      <span>{attachment.name}</span>
                      <em>Remover</em>
                      <XCircle size={14} />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="runs" ref={runsRef}>
              {!chatItems.length && (
                <article className="chat-turn empty-chat-turn">
                  <div className="chat-message assistant-message">
                    <div className="chat-meta">
                      <strong>Codex</strong>
                      <time>{formatChatTime(new Date())}</time>
                    </div>
                    <p>
                      {isChatTranscriptRestoring
                        ? "Restaurando a conversa deste projeto..."
                        : "Envie um prompt para criar plano, editar o projeto ou cole uma imagem para leitura visual."}
                    </p>
                  </div>
                </article>
              )}
              {chatItems.map(({ id, type, time, item, linkedJobs = [] }) => {
                if (type === "publication") {
                  return (
                    <article key={id} className={`chat-turn system-turn publication-turn ${publicationStateTone}`}>
                      <div className="chat-message assistant-message system-message">
                        <div className="chat-meta">
                          <strong>Estado do projeto</strong>
                          <time>{formatChatTime(new Date())}</time>
                        </div>
                        <div className="chat-status-line">
                          <span className="status-pill">{publication?.label || "Status"}</span>
                          {publicationRiskText && <small>{publicationRiskText}</small>}
                        </div>
                        <p className="chat-text multiline">{item.title}</p>
                        <p className="chat-text multiline">{item.detail}</p>
                        {item.note && <p className="chat-text multiline progress-text">{item.note}</p>}
                        <div className="run-actions">
                          {chatOverviewAction?.kind === "validate" && (
                            <button onClick={() => runProjectValidationAction()}><ShieldCheck size={14} />{chatOverviewAction.label}</button>
                          )}
                          {chatOverviewAction?.kind === "logs" && (
                            <button onClick={() => runPublicationUiAction("logs")}><History size={14} />{chatOverviewAction.label}</button>
                          )}
                          {chatOverviewAction?.kind === "publish" && (
                            <button onClick={() => setShowPublish(true)}><Rocket size={14} />{chatOverviewAction.label}</button>
                          )}
                          {chatOverviewAction?.kind === "primary_context" && (
                            <button onClick={() => openPublicationPrimaryContext()}><Activity size={14} />{chatOverviewAction.label}</button>
                          )}
                          {chatOverviewAction?.kind === "status" && (
                            <button onClick={() => runAction("status")} disabled={busy}><RefreshCcw size={14} />{chatOverviewAction.label}</button>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                }
                if (type === "request") {
                  return (
                    <article key={id} className="chat-turn synthetic-request-turn">
                      <div className="chat-message user-message">
                        <div className="chat-meta">
                          <strong>Você</strong>
                          <time>{formatChatTime(time || item.created_at || new Date())}</time>
                        </div>
                        <p>{item.text}</p>
                      </div>
                    </article>
                  );
                }
                if (type === "event") {
                  const event = item;
                  const eventActivity = event.activityItem || null;
                  const eventCompletionAction = explicitTurnNextStep(event) || (turnOwnsPrimaryAction(publication, "action_event", event.id)
                    ? { label: publication?.validation?.actionLabel || "Validar projeto", kind: "validate" }
                    : null);
                  return (
                    <article key={id} className={`chat-turn system-turn ${runStatusMeta(eventActivity?.status || (event.ok ? "completed" : "failed")).tone}`}>
                      <div className="chat-message assistant-message system-message">
                        <div className="chat-meta">
                          <strong>Sistema</strong>
                          <time>{formatChatTime(event.created_at)}</time>
                        </div>
                        <div className="chat-status-line">
                          <span className="status-pill">
                            {eventActivity?.statusLabel || (event.ok ? "Concluído" : "Falhou")}
                          </span>
                          <small>{eventActivity?.title || operationLabel(event.action)}</small>
                        </div>
                        {eventActivity?.summary ? (
                          <p className="chat-text multiline">{eventActivity.summary}</p>
                        ) : (
                          <p className="chat-text multiline">{event.summary || `${operationLabel(event.action)} registrada.`}</p>
                        )}
                        {eventActivity?.detail && <p className="chat-text multiline progress-text">{eventActivity.detail}</p>}
                        {event.nextStep?.detail && <p className="chat-text multiline progress-text">Próximo passo: {event.nextStep.detail}</p>}
                        {eventCompletionAction && (
                          <div className="run-actions">
                            <button onClick={() => runProjectValidationAction()}><ShieldCheck size={14} />{eventCompletionAction.label}</button>
                          </div>
                        )}
                        {!!linkedJobs.length && (
                          <div className="linked-job-list">
                            {linkedJobs.map((job) => {
                              const linkedJobActivity = job.activityItem || null;
                              const linkedJobOwnsPublicationAction = turnOwnsPrimaryAction(publication, "action_job", job.id);
                              const linkedJobCompletionAction = linkedJobOwnsPublicationAction
                                ? terminalJobCompletionAction(job, publication)
                                : null;
                              return (
                                <div key={job.id} className={`linked-job-step ${runStatusMeta(job.status).tone}`}>
                                  <div className="chat-meta">
                                    <strong>{linkedJobActivity?.title || operationLabel(job.action)}</strong>
                                    <time>{formatChatTime(job.completed_at || job.updated_at || job.created_at)}</time>
                                  </div>
                                  <div className="chat-status-line">
                                    <span className="status-pill">
                                      {["queued", "running"].includes(job.status) && <i />}
                                      {linkedJobActivity?.statusLabel || runStatusMeta(job.status).label}
                                    </span>
                                    {wasRecovered(job) && <span className="recovery-pill">Retomado após reinício</span>}
                                  </div>
                                  {linkedJobActivity?.summary ? (
                                    <p className="chat-text multiline">{linkedJobActivity.summary}</p>
                                  ) : job.progress_text ? (
                                    <p className="chat-text multiline">{cleanChatOutput(job.progress_text) || "Operação registrada."}</p>
                                  ) : (
                                    <p className="chat-text multiline">{`${operationLabel(job.action)} registrada.`}</p>
                                  )}
                                  {linkedJobActivity?.detail && <p className="chat-text multiline progress-text">{linkedJobActivity.detail}</p>}
                                  <div className="run-actions compact">
                                    {linkedJobCompletionAction?.kind === "validate" && (
                                      <button onClick={() => runProjectValidationAction()}><ShieldCheck size={14} />{linkedJobCompletionAction.label}</button>
                                    )}
                                    {linkedJobCompletionAction?.kind === "publish" && (
                                      <button onClick={() => setShowPublish(true)}><Rocket size={14} />{linkedJobCompletionAction.label}</button>
                                    )}
                                    {linkedJobCompletionAction?.kind === "logs" && (
                                      <button onClick={() => runPublicationUiAction("logs")}><History size={14} />{linkedJobCompletionAction.label}</button>
                                    )}
                                    {linkedJobCompletionAction?.kind === "primary_context" && (
                                      <button onClick={() => openPublicationPrimaryContext()}><Activity size={14} />{linkedJobCompletionAction.label}</button>
                                    )}
                                    {["queued", "running"].includes(job.status) && (
                                      <button onClick={() => cancelActionJob(job)}><XCircle size={14} />Cancelar</button>
                                    )}
                                    {job.result?.previewUrl && (
                                      <button onClick={() => { setDraftPreviewUrl(`${job.result.previewUrl}?t=${Date.now()}`); setActiveTab("preview"); }}><Eye size={14} />Ver preview</button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </article>
                  );
                }
                if (type === "job") {
                  const job = item;
                  const jobActivity = job.activityItem || null;
                  const jobFeedback = job.progress_text || "";
                  const jobCompletionAction = explicitTurnNextStep(job) || (turnOwnsPrimaryAction(publication, "action_job", job.id)
                    ? terminalJobCompletionAction(job, publication)
                    : null);
                  return (
                    <article key={id} className={`chat-turn system-turn ${runStatusMeta(job.status).tone}`}>
                      <div className="chat-message assistant-message system-message">
                        <div className="chat-meta">
                          <strong>Sistema</strong>
                          <time>{formatChatTime(job.completed_at || job.updated_at || job.created_at)}</time>
                        </div>
                      <div className="chat-status-line">
                        <span className="status-pill">
                          {["queued", "running"].includes(job.status) && <i />}
                          {jobActivity?.statusLabel || runStatusMeta(job.status, job).label}
                        </span>
                        {wasRecovered(job) && <span className="recovery-pill">Retomado após reinício</span>}
                        <small>{jobActivity?.title || operationLabel(job.action)}</small>
                        </div>
                        {jobActivity?.summary ? (
                          <p className="chat-text multiline">{jobActivity.summary}</p>
                        ) : jobFeedback ? (
                          <p className="chat-text multiline">{cleanChatOutput(jobFeedback) || "Operação registrada."}</p>
                        ) : (
                          <p>{`${operationLabel(job.action)} registrada.`}</p>
                        )}
                        {jobActivity?.detail && <p className="chat-text multiline progress-text">{jobActivity.detail}</p>}
                        {job.nextStep?.detail && <p className="chat-text multiline progress-text">Próximo passo: {job.nextStep.detail}</p>}
                        <div className="run-actions">
                          {jobCompletionAction?.kind === "validate" && (
                            <button onClick={() => runProjectValidationAction()}><ShieldCheck size={14} />{jobCompletionAction.label}</button>
                          )}
                          {jobCompletionAction?.kind === "publish" && (
                            <button onClick={() => setShowPublish(true)}><Rocket size={14} />{jobCompletionAction.label}</button>
                          )}
                          {jobCompletionAction?.kind === "logs" && (
                            <button onClick={() => runPublicationUiAction("logs")}><History size={14} />{jobCompletionAction.label}</button>
                          )}
                          {jobCompletionAction?.kind === "primary_context" && (
                            <button onClick={() => openPublicationPrimaryContext()}><Activity size={14} />{jobCompletionAction.label}</button>
                          )}
                          {["queued", "running"].includes(job.status) && (
                            <button onClick={() => cancelActionJob(job)}><XCircle size={14} />Cancelar</button>
                          )}
                          {job.result?.previewUrl && (
                            <button onClick={() => { setDraftPreviewUrl(`${job.result.previewUrl}?t=${Date.now()}`); setActiveTab("preview"); }}><Eye size={14} />Ver preview</button>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                }
                const run = item;
                const runActivity = run.activityItem || null;
                const runCompletionAction = explicitTurnNextStep(run) || (turnOwnsPrimaryAction(publication, "prompt_run", run.id)
                  ? terminalRunCompletionAction(run, publication)
                  : null);
                const runPreviewAction = terminalRunPreviewAction(run, actionJobs);
                return (
                  <article key={id} className={`chat-turn ${runStatusMeta(run.status, run).tone}`}>
                    <div className="chat-message user-message">
                      <div className="chat-meta">
                        <strong>Você</strong>
                        <time>{formatChatTime(run.created_at)}</time>
                      </div>
                      <p>{run.prompt}</p>
                      {run.attachments?.length > 0 && (
                        <div className="chat-attachments">
                          <strong>{run.attachments.length} imagem(ns) enviada(s)</strong>
                          {run.attachments.map((attachment, index) => (
                            <span key={`${attachment.name}-${index}`}>{attachment.name}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="chat-message assistant-message">
                      <div className="chat-meta">
                        <strong>{run.mode === "edit" ? "Codex editou" : "Codex planejou"}</strong>
                        <time>{formatChatTime(run.completed_at || run.updated_at || run.created_at)}</time>
                      </div>
                      <div className="chat-status-line">
                        <span className="status-pill">
                          {["queued", "running"].includes(run.status) && <i />}
                          {runActivity?.statusLabel || runStatusMeta(run.status, run).label}
                        </span>
                        {wasRecovered(run) && <span className="recovery-pill">Retomado após reinício</span>}
                        <small>{runActivity?.title || runStatusMeta(run.status, run).detail} · {run.model || "gpt-5.4"} · {run.reasoning_effort || "medium"}</small>
                      </div>
                      {run.commit_sha && (
                        <small className="commit-badge">commit {run.commit_sha.slice(0, 8)} · {run.pushed ? "push ok" : "push pendente"}</small>
                      )}
                      {runActivity?.summary ? (
                        <p className="chat-text multiline">{runActivity.summary}</p>
                      ) : run.result ? (
                        <p className="chat-text multiline">{cleanChatOutput(run.result) || "Alteração salva. Publicação automática em revisão."}</p>
                      ) : run.progress_text ? (
                        <p className="chat-text multiline">{cleanChatOutput(run.progress_text) || "Executando..."}</p>
                      ) : null}
                      {runActivity?.detail ? (
                        <p className="chat-text multiline progress-text">{runActivity.detail}</p>
                      ) : run.progress_text ? (
                        <p className="chat-text multiline progress-text">{cleanChatOutput(run.progress_text) || "Executando..."}</p>
                      ) : null}
                      {run.nextStep?.detail && <p className="chat-text multiline progress-text">Próximo passo: {run.nextStep.detail}</p>}
                      {!!linkedJobs.length && (
                        <div className="linked-job-list">
                          {linkedJobs.map((job) => {
                            const linkedJobActivity = job.activityItem || null;
                            const linkedJobCompletionAction = explicitTurnNextStep(job) || (turnOwnsPrimaryAction(publication, "action_job", job.id)
                              ? terminalJobCompletionAction(job, publication)
                              : null);
                            return (
                              <div key={job.id} className={`linked-job-step ${runStatusMeta(job.status).tone}`}>
                                <div className="chat-meta">
                                  <strong>{linkedJobActivity?.title || operationLabel(job.action)}</strong>
                                  <time>{formatChatTime(job.completed_at || job.updated_at || job.created_at)}</time>
                                </div>
                                <div className="chat-status-line">
                                  <span className="status-pill">
                                    {["queued", "running"].includes(job.status) && <i />}
                                    {linkedJobActivity?.statusLabel || runStatusMeta(job.status, job).label}
                                  </span>
                                  {wasRecovered(job) && <span className="recovery-pill">Retomado após reinício</span>}
                                  <small>{operationLabel(job.action)}</small>
                                </div>
                                {linkedJobActivity?.summary ? (
                                  <p className="chat-text multiline">{linkedJobActivity.summary}</p>
                                ) : job.progress_text ? (
                                  <p className="chat-text multiline">{cleanChatOutput(job.progress_text) || `${operationLabel(job.action)} registrada.`}</p>
                                ) : null}
                                {linkedJobActivity?.detail ? (
                                  <p className="chat-text multiline progress-text">{linkedJobActivity.detail}</p>
                                ) : null}
                                {job.nextStep?.detail && <p className="chat-text multiline progress-text">Próximo passo: {job.nextStep.detail}</p>}
                                <div className="run-actions">
                                  {linkedJobCompletionAction?.kind === "validate" && (
                                    <button onClick={() => runProjectValidationAction()}><ShieldCheck size={14} />{linkedJobCompletionAction.label}</button>
                                  )}
                                  {linkedJobCompletionAction?.kind === "publish" && (
                                    <button onClick={() => setShowPublish(true)}><Rocket size={14} />{linkedJobCompletionAction.label}</button>
                                  )}
                                  {linkedJobCompletionAction?.kind === "logs" && (
                                    <button onClick={() => runPublicationUiAction("logs")}><History size={14} />{linkedJobCompletionAction.label}</button>
                                  )}
                                  {linkedJobCompletionAction?.kind === "primary_context" && (
                                    <button onClick={() => openPublicationPrimaryContext()}><Activity size={14} />{linkedJobCompletionAction.label}</button>
                                  )}
                                  {["queued", "running"].includes(job.status) && (
                                    <button onClick={() => cancelActionJob(job)}><XCircle size={14} />Cancelar</button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="run-actions">
                        {(run.mode || "plan") === "plan" && run.status === "completed" && (
                          <button onClick={() => approveRun(run)} disabled={busy}><CheckCircle2 size={14} />Executar plano</button>
                        )}
                        {runCompletionAction?.kind === "validate" && (
                          <button onClick={() => runProjectValidationAction()}><ShieldCheck size={14} />{runCompletionAction.label}</button>
                        )}
                        {runCompletionAction?.kind === "publish" && (
                          <button onClick={() => setShowPublish(true)}><Rocket size={14} />{runCompletionAction.label}</button>
                        )}
                        {runCompletionAction?.kind === "logs" && (
                          <button onClick={() => runPublicationUiAction("logs")}><History size={14} />{runCompletionAction.label}</button>
                        )}
                        {runCompletionAction?.kind === "primary_context" && (
                          <button onClick={() => openPublicationPrimaryContext()}><Activity size={14} />{runCompletionAction.label}</button>
                        )}
                        {runPreviewAction?.kind === "preview" && (
                          <button onClick={() => { setDraftPreviewUrl(`${runPreviewAction.previewUrl}?t=${Date.now()}`); setActiveTab("preview"); }}>
                            <Eye size={14} />{runPreviewAction.label}
                          </button>
                        )}
                        {["queued", "running"].includes(run.status) && (
                          <button onClick={() => cancelRun(run)}><XCircle size={14} />Cancelar</button>
                        )}
                        {run.has_diff && (
                          <button onClick={() => revertRun(run)} disabled={busy}><Archive size={14} />Reverter</button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="panel stage-panel">
            {activeTab === "preview" && (
              <div className="preview-panel stage-content">
                {previewSurface}
              </div>
            )}

            {activeTab === "code" && (
              <div className="code-workspace stage-content">
                <section className="file-panel">
                  <div className="panel-head">
                    <span><FileCode2 size={16} />Arquivos</span>
                    <small>{flatTree.length} itens</small>
                  </div>
                  <div className="tree">
                    {flatTree.map((item) => (
                      <button
                        key={item.path}
                        className={`tree-row ${item.type}`}
                        style={{ paddingLeft: `${12 + item.depth * 14}px` }}
                        onClick={() => item.type === "file" && openFile(item.path)}
                        disabled={item.type === "dir"}
                      >
                        {item.type === "dir" ? "▸" : "•"} {item.path}
                      </button>
                    ))}
                  </div>
                </section>
                <section className="editor-panel">
                  <div className="panel-head">
                    <span><Code2 size={16} />{filePath || "Selecione um arquivo"}</span>
                    <button onClick={saveFile} disabled={!filePath || busy}><Save size={15} />Salvar</button>
                  </div>
                  <textarea spellCheck="false" value={fileContent} onChange={(event) => setFileContent(event.target.value)} />
                </section>
                <section className="diff-panel">
                  <div className="panel-head"><span><History size={16} />Diff</span></div>
                  <pre>{diff || "Sem alteracoes locais detectadas."}</pre>
                </section>
              </div>
            )}

            {activeTab === "data" && (
              <div className="infra-detail stage-content">
                {dataFocus && (
                  <section className={`operational-focus ${dataFocus.tone}`} data-focus-target={dataFocus.target}>
                    <div>
                      <strong>{dataFocus.title}</strong>
                      <span>{dataFocus.detail}</span>
                      {validationAction && <small>Validação: {validationAction.statusLabel}</small>}
                      <small>Próximo passo sugerido pelo editor.</small>
                    </div>
                    <button onClick={() => runPublicationUiAction(dataFocus.actionKind)} disabled={busy}>
                      <Activity size={14} />{dataFocus.actionLabel}
                    </button>
                  </section>
                )}
                {publication && (
                  <section className={`publication-summary data-section ${focusedDataSection === "summary" ? "focused" : ""} ${publicationStateTone}`} data-section="summary">
                    <strong>{publication.reasonLabel || publication.label}</strong>
                    <span>{publication.detail}</span>
                    {validationAction && <small>Validação: {validationAction.statusLabel}</small>}
                    <small>
                      {publication.label} · {publication.kind.replace(/_/g, " ")} · {publication.strategy.replace(/_/g, " ")}
                      {publication.domain ? ` · ${publication.domain}` : ""}
                    </small>
                  </section>
                )}
                <section className={`data-section ${focusedDataSection === "gates" ? "focused" : ""}`} data-section="gates">
                  <div className="panel-head">
                    <span><ShieldCheck size={16} />Checks técnicos</span>
                    <small>checks técnicos do projeto</small>
                  </div>
                {(infra?.gates || []).map((gate) => (
                  <div
                    key={gate.key}
                    className={`${gate.ok ? "gate ok" : "gate warn"} ${focusedGateKey === gate.key ? "focused" : ""}`}
                    data-gate-key={gate.key}
                  >
                    <strong>{gate.ok ? "OK" : "Atenção"}</strong>
                    <span>{gate.label}</span>
                    <small>{gate.detail}</small>
                  </div>
                ))}
                </section>
                <section className={`db-inventory data-section ${focusedDataSection === "db" ? "focused" : ""}`} data-section="db">
                  <div className="panel-head">
                    <span><Database size={16} />Schema Supabase/local</span>
                    <small>{infra?.databaseInventory?.detected ? "banco local detectado" : "sem banco detectado"}</small>
                  </div>
                  <div className="workflow-actions">
                    {validationAction && (
                      <button onClick={() => runProjectValidationAction()} disabled={!selected || busy} title={validationAction.detail}>
                        <ShieldCheck size={14} />{validationAction.label}
                      </button>
                    )}
                    <button onClick={() => runAction("db_status")} disabled={!selected || busy}><Activity size={14} />DB status</button>
                    <button onClick={() => runAction("db_migrate")} disabled={!selected || busy}><Database size={14} />Migration</button>
                    <button onClick={() => runAction("db_seed")} disabled={!selected || busy}><Database size={14} />Seed</button>
                    <button onClick={() => runAction("db_logs")} disabled={!selected || busy}><History size={14} />Logs</button>
                    <button onClick={() => runAction("db_start")} disabled={!selected || busy}><Rocket size={14} />Iniciar</button>
                    <button onClick={() => runAction("db_stop")} disabled={!selected || busy}><XCircle size={14} />Parar</button>
                  </div>
                  {!!infra?.databaseInventory?.publicTablesWithoutRlsList?.length && (
                    <div className="rls-risk-panel">
                      <strong>Tabelas public sem RLS</strong>
                      <span>
                        {(infra.databaseInventory.publicTablesWithoutRlsList || []).join(", ")}
                      </span>
                      <small>
                        {(infra.databaseInventory.exposedPublicTablesWithoutRlsList || []).length
                          ? `Expostas para anon/auth: ${infra.databaseInventory.exposedPublicTablesWithoutRlsList.join(", ")}`
                          : "Nenhuma tabela sem RLS exposta para anon/auth."}
                      </small>
                    </div>
                  )}
                  {(infra?.databaseInventory?.tables || []).slice(0, 18).map((table) => (
                    <article key={`${table.schema}.${table.name}`} className={tableSecurityClass(table)}>
                      <div>
                        <strong>{table.schema}.{table.name}</strong>
                        <span>{table.kind} · {table.columns?.length || 0} colunas · {table.policyCount || 0} policies</span>
                        <small>{formatClientAccess(table)}</small>
                      </div>
                      <small>{tableSecurityLabel(table)}</small>
                    </article>
                  ))}
                  {infra?.databaseInventory?.detected && !(infra.databaseInventory.tables || []).length && (
                    <p>Nenhuma tabela pública/auth/storage detectada.</p>
                  )}
                  {!infra?.databaseInventory?.detected && (
                    <p>{infra?.databaseInventory?.summary || "Banco local não detectado para este projeto."}</p>
                  )}
                </section>
                <section className={`release-history data-section ${focusedDataSection === "deployments" ? "focused" : ""}`} data-section="deployments">
                  <div className="panel-head">
                    <span><Rocket size={16} />Versões publicadas</span>
                    <small>{deployments.length} versões</small>
                  </div>
                  {deployments.map((deployment) => (
                    <article
                      key={deployment.id}
                      className={`${deployment.active ? "active" : ""} ${String(focusedDeploymentId || "") === String(deployment.id) ? "focused" : ""}`.trim()}
                      data-deployment-id={deployment.id}
                    >
                      <div>
                        <strong>{deployment.active ? "Publicado agora" : "Versão anterior"}</strong>
                        <span>{deployment.source_sha ? deployment.source_sha.slice(0, 8) : "sem sha"} · {deployment.branch || "sem branch"}</span>
                        <small>{formatDate(deployment.build_time || deployment.created_at)} · versão {deployment.version || "registrada"}</small>
                      </div>
                      <button onClick={() => rollbackDeployment(deployment)} disabled={busy || deployment.active}>
                        <Archive size={14} />Publicar versão
                      </button>
                    </article>
                  ))}
                  {!deployments.length && <p>Nenhuma versão publicada ainda.</p>}
                </section>
                <section className="infra-summary data-section" data-section="environment">
                  <div className="panel-head">
                    <span><ShieldCheck size={16} />Resumo do ambiente</span>
                    <small>{infra?.published ? "publicado" : "sem publicação"}</small>
                  </div>
                  <article>
                    <strong>Supabase/local</strong>
                    <span>{infra?.supabase?.detected ? "Detectado" : "Não detectado"}</span>
                    <small>{infra?.supabase?.apiAvailable ? "API local disponível" : "API local não detectada"}</small>
                  </article>
                  <article>
                    <strong>Serviços</strong>
                    <span>{infra?.containers?.running || 0} ativos de {infra?.containers?.count || 0}</span>
                    <small>{infra?.databases?.count || 0} banco(s) local(is) encontrado(s)</small>
                  </article>
                  <article>
                    <strong>GitHub</strong>
                    <span>{infra?.github?.private ? "Privado confirmado" : "Precisa de revisão"}</span>
                    <small>{infra?.git?.remoteSynced ? "Sincronizado com origin" : "Sincronização pendente"}</small>
                  </article>
                  <article>
                    <strong>Workflow do projeto</strong>
                    <span>{infra?.workflow?.publishStrategy || "auto"} · {infra?.workflow?.previewStrategy || "auto"}</span>
                    <small>
                      banco {infra?.workflow?.dbKind || "auto"}
                      {infra?.workflow?.migrationCmd ? ` · migrate: ${infra.workflow.migrationCmd}` : ""}
                    </small>
                  </article>
                </section>
              </div>
            )}

            {activeTab === "usage" && (
              <div className="usage-panel stage-content">
                <section className="usage-summary">
                  <article>
                    <span>Sistema</span>
                    <strong>{formatNumber(usage?.summary?.total_tokens)}</strong>
                    <small>{formatNumber(usage?.summary?.runs)} prompts · {formatNumber(usage?.summary?.estimated_runs)} estimados</small>
                  </article>
                  <article>
                    <span>Projeto atual</span>
                    <strong>{formatNumber(selectedUsage?.total_tokens)}</strong>
                    <small>{selected?.name || "sem projeto"} · {formatNumber(selectedUsage?.runs)} prompts</small>
                  </article>
                  <article>
                    <span>Entrada</span>
                    <strong>{formatNumber(usage?.summary?.prompt_tokens)}</strong>
                    <small>tokens de prompt no histórico</small>
                  </article>
                  <article>
                    <span>Saída</span>
                    <strong>{formatNumber(usage?.summary?.completion_tokens)}</strong>
                    <small>tokens de resposta/execução</small>
                  </article>
                </section>

                <section className="usage-grid">
                  <div className="usage-card">
                    <div className="panel-head">
                      <span><Activity size={16} />Consumo por projeto</span>
                      <small>{usage?.projects?.length || 0} projetos</small>
                    </div>
                    <div className="usage-table">
                      {(usage?.projects || []).map((project) => (
                        <article key={project.project_id} className={project.project_id === selectedId ? "active" : ""}>
                          <div>
                            <strong>{project.name}</strong>
                            <span>{project.domain || project.project_id}</span>
                          </div>
                          <small>{formatNumber(project.total_tokens)} tokens</small>
                          <small>{formatNumber(project.runs)} prompts</small>
                        </article>
                      ))}
                      {!usage?.projects?.length && <p>Nenhum consumo registrado ainda.</p>}
                    </div>
                  </div>

                  <div className="usage-card">
                    <div className="panel-head">
                      <span><History size={16} />Histórico de prompts</span>
                      <small>{usage?.recent?.length || 0} registros</small>
                    </div>
                    <div className="usage-runs">
                      {(usage?.recent || []).map((run) => (
                        <article key={run.id}>
                          <div>
                            <strong>{run.project_name} · {run.mode === "edit" ? "Editar" : "Plano"}</strong>
                            <span>{run.prompt_preview || promptPreviewText(run.prompt)}</span>
                            <small>{formatChatTime(run.completed_at || run.created_at)} · {run.model || "gpt-5.4"} · {run.reasoning_effort || "medium"}{run.token_usage_estimated ? " · estimado" : ""}</small>
                          </div>
                          <strong>{formatNumber(run.total_tokens)}</strong>
                        </article>
                      ))}
                      {!usage?.recent?.length && <p>Nenhum prompt registrado ainda.</p>}
                    </div>
                  </div>
                </section>
              </div>
            )}

            {activeTab === "logs" && (
              <div className="log-stage stage-content">
                <div className="preview-panel log-preview-panel">
                  {previewSurface}
                </div>
                <aside className="log-panel">
                  {activityOverview && (
                    <section className={`operational-focus ${publicationStateTone}`} data-focus-target={publication?.primaryRef?.kind || publication?.targetSection || "activity"}>
                      <div>
                        <strong>{activityOverview.title}</strong>
                        <span>{activityOverview.detail}</span>
                        {validationAction && <small>Validação: {validationAction.statusLabel}</small>}
                        <small>{activityOverview.note}</small>
                      </div>
                      {activityOverview.actionKind === "refresh_status" ? (
                        <button onClick={() => runAction("status")} disabled={busy}>
                          <RefreshCcw size={14} />{activityOverview.actionLabel}
                        </button>
                      ) : activityOverview.actionKind === "open_publish" ? (
                        <button onClick={() => setShowPublish(true)} disabled={!selected || busy}>
                          <Rocket size={14} />{activityOverview.actionLabel}
                        </button>
                      ) : (
                        <button onClick={() => openPublicationPrimaryContext()} disabled={!selected || busy}>
                          <Activity size={14} />{activityOverview.actionLabel}
                        </button>
                      )}
                    </section>
                  )}
                  <div className="activity-timeline">
                    <div className="panel-head">
                      <span><History size={16} />Linha do tempo</span>
                      <small>{activity.length} eventos</small>
                    </div>
                    {activity.map((item) => (
                      <article
                        key={item.id}
                        className={`activity-item ${item.status || "idle"} ${item.id === focusedActivityId && publication?.primaryRef?.kind === "activity" ? "focused" : ""}`}
                        data-activity-id={item.id}
                      >
                        <div className="activity-body">
                          <div className="activity-headline">
                            <div className="activity-title-group">
                              <span className="activity-marker">{activityLabel(item.type)}</span>
                              <strong>{item.title}</strong>
                            </div>
                            <span className="activity-status">{item.statusLabel || runStatusMeta(item.status).label}</span>
                          </div>
                          <p className="activity-summary">{item.summary}</p>
                          {item.detail && <div className="activity-detail">{item.detail}</div>}
                          <div className="activity-meta">
                            <time>{formatDate(item.occurredAt)}</time>
                            {item.commitSha && <span>Commit {item.commitSha.slice(0, 8)}{item.pushed ? " enviado" : ""}</span>}
                          </div>
                        </div>
                      </article>
                    ))}
                    {!activity.length && <p>Nenhuma atividade registrada para este projeto.</p>}
                  </div>
                  <div className="release-history">
                    <div className="panel-head">
                      <span><Rocket size={16} />Versões publicadas</span>
                      <small>{deployments.length} versões</small>
                    </div>
                    {deployments.map((deployment) => (
                      <article
                        key={deployment.id}
                        className={`${deployment.active ? "active" : ""} ${String(focusedDeploymentId || "") === String(deployment.id) ? "focused" : ""}`.trim()}
                        data-deployment-id={deployment.id}
                      >
                        <div>
                          <strong>{deployment.active ? "Publicado agora" : "Versão anterior"}</strong>
                          <span>{deployment.source_sha ? deployment.source_sha.slice(0, 8) : "sem sha"} · {deployment.branch || "sem branch"}</span>
                          <small>{formatDate(deployment.build_time || deployment.created_at)}</small>
                        </div>
                        <button onClick={() => rollbackDeployment(deployment)} disabled={busy || deployment.active}>
                          <Archive size={14} />Publicar versão
                        </button>
                      </article>
                    ))}
                    {!deployments.length && <p>Nenhuma versão publicada ainda.</p>}
                  </div>
                </aside>
              </div>
            )}
          </section>
        </div>

        {showCreateProject && (
          <div className="drawer-backdrop" role="dialog" aria-modal="true">
            <section className="create-project-dialog">
              <div className="panel-head">
                <span><Plus size={17} />Novo projeto</span>
                <button onClick={() => setShowCreateProject(false)} disabled={creatingProject}>Fechar</button>
              </div>
              <div className="create-project-body">
                <label>
                  <span>Nome do projeto</span>
                  <input
                    value={createProjectDraft.name}
                    onChange={(event) => updateCreateProjectName(event.target.value)}
                    placeholder="CRM Oficina"
                  />
                </label>
                <label>
                  <span>Subdomínio</span>
                  <input
                    value={createProjectDraft.slug}
                    onChange={(event) => updateCreateProjectSlug(event.target.value)}
                    placeholder="crm-oficina"
                  />
                  <small>{createProjectDraft.slug ? `${createProjectDraft.slug}.axqi.ai` : "subdominio.axqi.ai"}</small>
                </label>
                <label>
                  <span>Prompt inicial</span>
                  <textarea
                    value={createProjectDraft.prompt}
                    onChange={(event) => setCreateProjectDraft((current) => ({ ...current, prompt: event.target.value }))}
                    placeholder="Descreva o sistema que deve nascer a partir do template padrão."
                  />
                </label>
              </div>
              <div className="project-config-actions">
                <button onClick={() => setShowCreateProject(false)} disabled={creatingProject}>Cancelar</button>
                <button className="danger" onClick={createProject} disabled={creatingProject || busy}>
                  <Rocket size={16} />{creatingProject ? "Criando..." : "Criar projeto"}
                </button>
              </div>
            </section>
          </div>
        )}

        {showImportProject && (
          <div className="drawer-backdrop" role="dialog" aria-modal="true">
            <section className="create-project-dialog">
              <div className="panel-head">
                <span><Globe2 size={17} />Importar do GitHub</span>
                <button onClick={() => setShowImportProject(false)} disabled={importingProject}>Fechar</button>
              </div>
              <div className="create-project-body">
                <label>
                  <span>Nome do projeto</span>
                  <input
                    value={importProjectDraft.name}
                    onChange={(event) => updateImportProjectName(event.target.value)}
                    placeholder="EGGSBOV"
                  />
                </label>
                <label>
                  <span>Subdomínio</span>
                  <input
                    value={importProjectDraft.slug}
                    onChange={(event) => updateImportProjectSlug(event.target.value)}
                    placeholder="eggsbov"
                  />
                  <small>{importProjectDraft.slug ? `${importProjectDraft.slug}.axqi.ai` : "subdominio.axqi.ai"}</small>
                </label>
                <label>
                  <span>URL do GitHub</span>
                  <input
                    value={importProjectDraft.repositoryUrl}
                    onChange={(event) => setImportProjectDraft((current) => ({ ...current, repositoryUrl: event.target.value }))}
                    placeholder="https://github.com/owner/repo.git"
                  />
                </label>
                <label>
                  <span>Prompt inicial opcional</span>
                  <textarea
                    value={importProjectDraft.prompt}
                    onChange={(event) => setImportProjectDraft((current) => ({ ...current, prompt: event.target.value }))}
                    placeholder="Se quiser, descreva a primeira alteração que o Codex já deve aplicar após a importação."
                  />
                </label>
              </div>
              <div className="project-config-actions">
                <button onClick={() => setShowImportProject(false)} disabled={importingProject}>Cancelar</button>
                <button className="danger" onClick={importProject} disabled={importingProject || busy}>
                  <Rocket size={16} />{importingProject ? "Importando..." : "Importar projeto"}
                </button>
              </div>
            </section>
          </div>
        )}

        {showProjectConfig && configDraft && (
          <div className="drawer-backdrop" role="dialog" aria-modal="true">
            <aside className="project-config-drawer">
              <div className="panel-head">
                <span><Settings2 size={17} />Projeto</span>
                <button onClick={() => setShowProjectConfig(false)}>Fechar</button>
              </div>
              <div className="project-config-body">
                {publication && (
                  <section className="project-config-section project-operational-section">
                    <div className="panel-head">
                      <span>Status operacional</span>
                      <small>{publication.label}</small>
                    </div>
                    <div className={`publication-summary ${publicationStateTone}`}>
                      <strong>{publication.reasonLabel || publication.label}</strong>
                      <span>{publication.detail}</span>
                      {validationAction && <small>Validação: {validationAction.statusLabel}</small>}
                      {publicationRiskText && <small>{publicationRiskText}</small>}
                    </div>
                    <div className="workflow-actions compact">
                      {projectDrawerAction && (
                        <button onClick={() => openPublicationPrimaryContext({ closeProjectConfig: true })} disabled={!selected || busy}>
                          <Activity size={14} />{projectDrawerAction.label}
                        </button>
                      )}
                      {validationAction && (
                        <button onClick={() => runProjectValidationAction({ closeProjectConfig: true })} disabled={!selected || busy} title={validationAction.detail}>
                          <ShieldCheck size={14} />{validationAction.label}
                        </button>
                      )}
                    </div>
                  </section>
                )}
                <section className="project-config-section">
                  <div className="panel-head">
                    <span>Workflow</span>
                    <small>{selected?.name || "sem projeto"}</small>
                  </div>
                  <div className="project-config-grid">
                    <label>
                      <span>Preview</span>
                      <select value={configDraft.previewStrategy} onChange={(event) => setConfigDraft((current) => ({ ...current, previewStrategy: event.target.value }))}>
                        <option value="auto">auto</option>
                        <option value="production_domain">production_domain</option>
                        <option value="static_build">static_build</option>
                        <option value="runtime_proxy">runtime_proxy</option>
                      </select>
                    </label>
                    <label>
                      <span>Publicação</span>
                      <select value={configDraft.publishStrategy} onChange={(event) => setConfigDraft((current) => ({ ...current, publishStrategy: event.target.value }))}>
                        <option value="auto">auto</option>
                        <option value="deploy_script">deploy_script</option>
                        <option value="static_release">static_release</option>
                        <option value="runtime_restart">runtime_restart</option>
                      </select>
                    </label>
                    <label>
                      <span>Banco</span>
                      <select value={configDraft.dbKind} onChange={(event) => setConfigDraft((current) => ({ ...current, dbKind: event.target.value }))}>
                        <option value="auto">auto</option>
                        <option value="supabase">supabase</option>
                        <option value="postgres">postgres</option>
                        <option value="mysql">mysql</option>
                        <option value="none">none</option>
                      </select>
                    </label>
                    <label>
                      <span>Container DB</span>
                      <input value={configDraft.dbContainer} onChange={(event) => setConfigDraft((current) => ({ ...current, dbContainer: event.target.value }))} placeholder="ggrc-supabase-db" />
                    </label>
                    <label className="span-2">
                      <span>Migration command</span>
                      <input value={configDraft.migrationCmd} onChange={(event) => setConfigDraft((current) => ({ ...current, migrationCmd: event.target.value }))} placeholder="npm run db:migrate" />
                    </label>
                    <label className="span-2">
                      <span>Seed command</span>
                      <input value={configDraft.seedCmd} onChange={(event) => setConfigDraft((current) => ({ ...current, seedCmd: event.target.value }))} placeholder="npm run db:seed" />
                    </label>
                    <label className="span-2">
                      <span>Serviços runtime</span>
                      <textarea value={configDraft.runtimeServicesText} onChange={(event) => setConfigDraft((current) => ({ ...current, runtimeServicesText: event.target.value }))} placeholder="um por linha" />
                    </label>
                    <label className="span-2">
                      <span>Comandos de validação</span>
                      <textarea value={configDraft.validationCmdsText} onChange={(event) => setConfigDraft((current) => ({ ...current, validationCmdsText: event.target.value }))} placeholder="um por linha" />
                    </label>
                    <label className="span-2">
                      <span>Notas</span>
                      <textarea value={configDraft.notes} onChange={(event) => setConfigDraft((current) => ({ ...current, notes: event.target.value }))} placeholder="observações do projeto" />
                    </label>
                  </div>
                </section>
                <section className="project-config-section">
                  <div className="panel-head">
                    <span>Banco</span>
                    <small>ações operacionais</small>
                  </div>
                  <div className="workflow-actions compact">
                    <button onClick={() => runAction("db_status")} disabled={!selected || busy}><Activity size={14} />DB status</button>
                    <button onClick={() => runAction("db_migrate")} disabled={!selected || busy}><Database size={14} />Migration</button>
                    <button onClick={() => runAction("db_seed")} disabled={!selected || busy}><Database size={14} />Seed</button>
                    <button onClick={() => runAction("db_logs")} disabled={!selected || busy}><History size={14} />Logs</button>
                    <button onClick={() => runAction("db_start")} disabled={!selected || busy}><Rocket size={14} />Iniciar</button>
                    <button onClick={() => runAction("db_stop")} disabled={!selected || busy}><XCircle size={14} />Parar</button>
                  </div>
                </section>
              </div>
              <div className="project-config-actions">
                <button onClick={() => setShowProjectConfig(false)}>Fechar</button>
                <button className="danger" onClick={saveProjectConfig} disabled={savingConfig || busy}>
                  <Save size={16} />Salvar
                </button>
              </div>
            </aside>
          </div>
        )}

        {showPublish && (
          <div className="modal-backdrop" role="dialog" aria-modal="true">
            <section className="publish-modal">
              <div className="panel-head">
                <span><Rocket size={17} />{publication?.nextAction === "publish" ? "Publicar alterações" : "Painel de publicação"}</span>
                <button onClick={() => setShowPublish(false)}>Fechar</button>
              </div>
              <div className="publish-body">
                <p>{publication?.nextAction === "publish" ? "Esta ação publica a versão atual do projeto selecionado no domínio real." : "Revise o estado operacional e as versões publicadas deste projeto."}</p>
                <div className="publish-target">
                  <strong>{selected?.domain || selected?.name}</strong>
                  <small>{publication?.detail || (activeDeployment ? `Versão ${activeDeployment.version || formatDate(activeDeployment.build_time || activeDeployment.created_at)}` : "sem versão publicada")}</small>
                </div>
                {validationAction && (
                  <div className="publish-validation">
                    <div>
                      <strong>{validationAction.statusLabel}</strong>
                      <span>{validationAction.detail}</span>
                      <small>{validationAction.note}</small>
                    </div>
                    <button type="button" onClick={() => runProjectValidationAction({ closePublish: true })} disabled={!selected || busy} className={validationAction.recommended ? "recommended" : ""}>
                      <ShieldCheck size={14} />{validationAction.label}
                    </button>
                  </div>
                )}
                {publication && (
                  <div className={`publication-summary ${publicationStateTone}`}>
                    <strong>{publication.label}</strong>
                    <span>{publication.detail}</span>
                    <small>
                      Próxima ação sugerida: {publication.nextAction === "none" ? "nenhuma" : publication.nextAction.replace(/_/g, " ")}
                      {validationAction ? ` · validação ${validationAction.statusLabel.toLowerCase()}` : ""}
                    </small>
                  </div>
                )}
                <div className="publish-gates">
                  {(infra?.gates || []).map((gate) => (
                    <div key={gate.key} className={gate.ok ? "gate ok" : "gate warn"}>
                      <strong>{gate.ok ? "OK" : "Atenção"}</strong>
                      <span>{gate.label}</span>
                      <small>{gate.detail}</small>
                    </div>
                  ))}
                </div>
                {criticalPublishBlockers.length > 0 && (
                  <div className="notice">
                    Publicação bloqueada até corrigir: {criticalPublishBlockers.map((gate) => gate.label).join(", ")}.
                  </div>
                )}
                <div className="release-history compact">
                  <div className="panel-head">
                    <span><History size={16} />Versões recentes</span>
                    <small>{deployments.length} versões</small>
                  </div>
                  {deployments.slice(0, 4).map((deployment) => (
                    <article
                      key={deployment.id}
                      className={`${deployment.active ? "active" : ""} ${String(focusedDeploymentId || "") === String(deployment.id) ? "focused" : ""}`.trim()}
                      data-deployment-id={deployment.id}
                    >
                      <div>
                        <strong>{deployment.active ? "Publicado agora" : "Versão anterior"}</strong>
                        <span>{deployment.source_sha ? deployment.source_sha.slice(0, 8) : "sem sha"} · {deployment.branch || "sem branch"}</span>
                        <small>{formatDate(deployment.build_time || deployment.created_at)} · versão {deployment.version || "registrada"}</small>
                      </div>
                      <button onClick={() => rollbackDeployment(deployment)} disabled={busy || deployment.active}>
                        <Archive size={14} />Publicar versão
                      </button>
                    </article>
                  ))}
                  {!deployments.length && <p>Nenhuma versão publicada ainda.</p>}
                </div>
                <pre>{diff || "Sem diff local detectado."}</pre>
              </div>
              <div className="publish-actions">
                <button onClick={() => setShowPublish(false)}>Cancelar</button>
                <button
                  className={modalPublicationAction.kind === "publish" ? "danger" : ""}
                  onClick={confirmPublicationModalAction}
                  disabled={busy || modalPublicationAction.disabled || (modalPublicationAction.kind === "publish" && criticalPublishBlockers.length > 0)}
                >
                  <Rocket size={16} />{modalPublicationAction.label}
                </button>
              </div>
            </section>
          </div>
        )}
      </section>
    </main>
  );
}

createRoot(document.getElementById("root")).render(<App />);
